import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createFifaRoom,
  decideFifaHistoricalImport,
  joinFifaRoom,
  loadDefaultFifaRoom,
  loadFifaLeagueDetails,
  loadFifaRoom,
  loadMyFifaRooms,
  loadPublicFifaPreview,
  saveFifaPrediction,
  saveFifaResult,
  saveFifaSpecialPrediction,
  saveFifaSpecialResult,
  updateFifaRoom,
  updateFifaTournamentSpecials,
} from "./fifaApi";
import { FIFA_WORLD_CUP_2026_MATCHES } from "./fifaWorldCup2026Matches";
import {
  CopyCodeIcon,
  LeaveDoorIcon,
  LinkChainIcon,
  PenIcon,
  QuickIconButton,
} from "../whiteboard/components/WhiteboardControlPrimitives";
import "./FifaPage.css";

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("pl-PL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function warsawDateKey(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

const KNOCKOUT_STAGE_LABELS = {
  round_of_32: "1/16 finału",
  round_of_16: "1/8 finału",
  quarterfinal: "Ćwierćfinały",
  semifinal: "Półfinały",
  third_place: "Mecz o 3. miejsce",
  final: "Finał",
};

const KNOCKOUT_STAGE_ORDER = Object.keys(KNOCKOUT_STAGE_LABELS);

const SCORING_PREVIEW_PHASES = [
  { key: "group", label: "Faza grupowa", level: 0 },
  { key: "round_of_32", label: "1/16 finału", level: 1 },
  { key: "round_of_16", label: "1/8 finału", level: 2 },
  { key: "quarterfinal", label: "Ćwierćfinały", level: 3 },
  { key: "semifinal", label: "Półfinały", level: 4 },
  { key: "third_place", label: "Mecz o 3. miejsce", level: 5 },
  { key: "final", label: "Finał", level: 5 },
];

function createManualPhasePoints() {
  return Object.fromEntries(
    SCORING_PREVIEW_PHASES.map((phase) => [
      phase.key,
      {
        exactScore: 3 + phase.level,
        closeScore: 1.5 + phase.level * 0.5,
        correctOutcome: 1 + phase.level * 0.5,
      },
    ])
  );
}

const SCORING_PRESETS = [
  {
    key: "classic",
    label: "Klasyczny",
    description: "Dokładny wynik i rezultat",
    closeScoreEnabled: false,
    firstScorerEnabled: false,
  },
  {
    key: "close",
    label: "Z bliskim wynikiem",
    description: "Klasyczny plus bliski wynik",
    closeScoreEnabled: true,
    firstScorerEnabled: false,
  },
  {
    key: "scorer",
    label: "Z pierwszym strzelcem",
    description: "Klasyczny plus pierwszy strzelec",
    closeScoreEnabled: false,
    firstScorerEnabled: true,
  },
  {
    key: "full",
    label: "Pełny",
    description: "Bliski wynik i pierwszy strzelec",
    closeScoreEnabled: true,
    firstScorerEnabled: true,
  },
];

function stageLabel(stage) {
  return KNOCKOUT_STAGE_LABELS[stage] || "Mecze";
}

function groupLabel(group) {
  return group === "TEST" ? "Test" : `Grupa ${group}`;
}

function scoringPreviewValue(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
}

function formatPointValue(value) {
  return new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 1 }).format(
    scoringPreviewValue(value)
  );
}

function scoringRulesSummary(rules = {}) {
  const parts = [`dokładny wynik ${rules.exactScore ?? 3} pkt`];
  if (rules.closeScoreEnabled === true) {
    parts.push(`bliski wynik ${rules.closeScore ?? 1.5} pkt`);
  }
  parts.push(`rezultat ${rules.correctOutcome ?? 1} pkt`);
  return parts.join(", ");
}

function hasKnownParticipants(match) {
  if (match?.group) return true;
  const placeholderPattern =
    /^(?:\d+\.\s*miejsce|3\.\s*miejsce|zwycięzca meczu|przegrany meczu)/i;
  return (
    Boolean(match?.homeTeam) &&
    Boolean(match?.awayTeam) &&
    !placeholderPattern.test(match.homeTeam) &&
    !placeholderPattern.test(match.awayTeam)
  );
}

function predictionProgress(matches, predictions) {
  const availableMatches = matches.filter(hasKnownParticipants);
  const predicted = availableMatches.filter((match) => {
    const prediction = predictions?.[match.id];
    return prediction?.homeGoals != null && prediction?.awayGoals != null;
  }).length;
  const waiting = matches.length - availableMatches.length;
  return {
    predicted,
    available: availableMatches.length,
    waiting,
  };
}

function PredictionProgress({ matches, predictions }) {
  const progress = predictionProgress(matches, predictions);
  return (
    <span className="fifa-prediction-progress">
      Wytypowane: {progress.predicted}/{progress.available}
      {progress.waiting ? ` · ${progress.waiting} oczekuje na uczestników` : ""}
    </span>
  );
}

function getMyStanding(data) {
  const userId = String(data?.me?.userId || "");
  const rows = Array.isArray(data?.leaderboard) ? data.leaderboard : [];
  return rows.find((row) => String(row.userId || "") === userId) || null;
}

function rankLabel(rank, exAequo = false) {
  if (!rank) return "bez miejsca";
  return `${rank}. miejsce${exAequo ? " ex aequo" : ""}`;
}

// Polska odmiana rzeczownika "typ": 1 typ, 2-4 typy, 0/5+ typów.
function typyLabel(count) {
  const n = Math.abs(Number(count) || 0);
  if (n === 1) return "1 typ";
  const lastTwo = n % 100;
  const last = n % 10;
  const isFew = last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14);
  return `${n} ${isFew ? "typy" : "typów"}`;
}

function fifaErrorMessage(error, fallback) {
  const code = error?.message || error;
  if (code === "room_not_found") return "Nie znaleziono ligi o takim kodzie.";
  if (code === "league_owner_only") return "Tylko administrator tej ligi może zmienić jej nazwę.";
  if (code === "invalid_title") return "Podaj poprawną nazwę ligi.";
  if (code === "prediction_locked") return "Typowanie tego meczu jest już zamknięte.";
  if (code === "participants_not_confirmed") {
    return "Typowanie będzie dostępne po ustaleniu uczestników meczu.";
  }
  if (code === "first_scorer_requires_zero_zero") {
    return 'Opcja "Nikt nie strzeli" wymaga wyniku 0:0.';
  }
  if (code === "zero_zero_excludes_first_scorer") {
    return "Przy wyniku 0:0 nie można wskazać zawodnika ani gola samobójczego.";
  }
  if (code === "first_scorer_team_has_no_goal") {
    return "Wybrana drużyna musi mieć w typowanym wyniku co najmniej jednego gola.";
  }
  if (code === "invalid_first_scorer_player") {
    return "Wybrany zawodnik nie znajduje się w aktualnej kadrze tej drużyny.";
  }
  if (code === "invalid_first_scorer_team") {
    return "Wybrano nieprawidłową drużynę pierwszego strzelca.";
  }
  if (code === "special_bets_locked") {
    return "Zakłady turniejowe są już zamknięte.";
  }
  if (code === "special_bets_disabled") {
    return "Ta liga nie korzysta z zakładów turniejowych.";
  }
  if (code === "special_bets_workspace_only") {
    return "Zakłady turniejowe zapisuje się w głównym typerze.";
  }
  if (code === "invalid_golden_boot_player") {
    return "Wybierz zawodnika z listy oficjalnych kadr.";
  }
  if (code === "invalid_champion_team") {
    return "Wybierz reprezentację uczestniczącą w turnieju.";
  }
  if (code === "empty_special_prediction") {
    return "Wybierz mistrza świata lub króla strzelców.";
  }
  if (typeof code === "string" && /\s/.test(code)) return code;
  return fallback || "Coś poszło nie tak.";
}

function FifaSkeleton() {
  return (
    <div className="fifa-shell">
      <div className="glass-container fifa-page">
        <div className="fifa-skeleton-header">
          <div className="fifa-skeleton-line fifa-skeleton-line--title" />
          <div className="fifa-skeleton-line" />
        </div>
        <div className="panel-card fifa-panel">
          <div className="fifa-skeleton-line fifa-skeleton-line--wide" />
          <div className="fifa-skeleton-line" />
        </div>
        <div className="fifa-summary-grid fifa-summary-grid--hidden">
          <div className="panel-card">
            <div className="fifa-skeleton-line fifa-skeleton-line--short" />
            <div className="fifa-skeleton-line fifa-skeleton-line--stat" />
          </div>
          <div className="panel-card">
            <div className="fifa-skeleton-line fifa-skeleton-line--short" />
            <div className="fifa-skeleton-line fifa-skeleton-line--wide" />
          </div>
        </div>
        <div className="fifa-grid">
          <div className="fifa-match-list">
            {[0, 1].map((item) => (
              <div className="fifa-match-card fifa-match-card--loading" key={item}>
                <div className="fifa-skeleton-line fifa-skeleton-line--wide" />
                <div className="fifa-skeleton-line" />
                <div className="fifa-skeleton-line fifa-skeleton-line--short" />
              </div>
            ))}
          </div>
          <div className="panel-card">
            <div className="fifa-skeleton-line fifa-skeleton-line--short" />
            <div className="fifa-skeleton-line fifa-skeleton-line--wide" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreInput({ value, disabled, onChange, ariaLabel }) {
  const normalize = (next) => {
    if (next === "") return "";
    const n = Math.max(0, Math.min(50, Number(next)));
    if (!Number.isFinite(n)) return "";
    return String(Math.trunc(n));
  };

  const step = (delta) => {
    if (disabled) return;
    if (value === "" || value == null) {
      onChange("0");
      return;
    }
    onChange(normalize(Number(value) + delta));
  };

  return (
    <div className="fifa-score-stepper">
      <input
        className="input-secondary fifa-scorebox"
        type="number"
        min="0"
        max="50"
        step="1"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(normalize(e.target.value))}
      />
      <div className="fifa-stepper-buttons" aria-hidden="true">
        <button type="button" tabIndex={-1} disabled={disabled} onClick={() => step(1)}>
          ▲
        </button>
        <button type="button" tabIndex={-1} disabled={disabled} onClick={() => step(-1)}>
          ▼
        </button>
      </div>
    </div>
  );
}

function emptyFirstScorer() {
  return { kind: "none", playerId: null, playerName: null, team: null };
}

function firstScorerKey(firstScorer) {
  const scorer = firstScorer || emptyFirstScorer();
  return [scorer.kind, scorer.playerId || "", scorer.team || ""].join("|");
}

function firstScorerLabel(firstScorer) {
  const scorer = firstScorer || emptyFirstScorer();
  if (scorer.kind === "no_goal") return "Pierwszy gol: nikt";
  if (scorer.kind === "own_goal") return `Pierwszy gol: samobój dla ${scorer.team}`;
  if (scorer.kind === "player") {
    const teamLabel = scorer.team ? ` (${scorer.team})` : "";
    return `Pierwszy gol: ${scorer.playerName || "wybrany zawodnik"}${teamLabel}`;
  }
  return "";
}

function actualFirstScorerLabel(match) {
  if (match?.result?.status !== "finished") return "";
  const scorerLabel = firstScorerLabel(match.result.firstScorer);
  if (scorerLabel) return scorerLabel;
  if (Number(match.result.homeGoals) === 0 && Number(match.result.awayGoals) === 0) {
    return "Pierwszy gol: nikt";
  }
  return "Pierwszy gol: nie podano";
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pl");
}

function validateScorerForScore(firstScorer, homeGoals, awayGoals, match) {
  const scorer = firstScorer || emptyFirstScorer();
  if (scorer.kind === "none") return "";
  if (homeGoals === "" || homeGoals == null || awayGoals === "" || awayGoals == null) return "";
  const home = Number(homeGoals);
  const away = Number(awayGoals);
  if (scorer.kind === "no_goal" && (home !== 0 || away !== 0)) {
    return 'Opcja "Nikt nie strzeli" wymaga wyniku 0:0.';
  }
  if (scorer.kind !== "no_goal" && home === 0 && away === 0) {
    return "Przy wyniku 0:0 nie można wskazać strzelca.";
  }
  if (
    (scorer.team === match.homeTeam && home === 0) ||
    (scorer.team === match.awayTeam && away === 0)
  ) {
    return "Wybrana drużyna musi mieć co najmniej jednego gola.";
  }
  return "";
}

function FirstScorerField({
  value,
  match,
  playersByTeam,
  disabled,
  label = "Typuję pierwszego strzelca",
  onChange,
}) {
  const pickerRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const scorer = value || emptyFirstScorer();
  const enabled = scorer.kind !== "none";
  const homePlayers = playersByTeam?.[match.homeTeam] || [];
  const awayPlayers = playersByTeam?.[match.awayTeam] || [];
  const normalizedSearch = normalizeSearchText(search);
  const filterPlayers = (players) =>
    normalizedSearch
      ? players.filter((player) =>
          normalizeSearchText(player.fullName).includes(normalizedSearch)
        )
      : players;
  const filteredHomePlayers = filterPlayers(homePlayers);
  const filteredAwayPlayers = filterPlayers(awayPlayers);
  const selectedLabel =
    scorer.kind === "no_goal"
      ? "Nikt nie strzeli (0:0)"
      : scorer.kind === "own_goal"
        ? `Gol samobójczy dla ${scorer.team}`
        : scorer.kind === "player"
          ? scorer.playerName || "Wybrany zawodnik"
          : "Wybierz pierwszego strzelca";

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [pickerOpen]);

  useEffect(() => {
    if (!enabled) {
      setPickerOpen(false);
      setSearch("");
    }
  }, [enabled]);

  const selectScorer = (next) => {
    onChange(next);
    setPickerOpen(false);
    setSearch("");
  };

  return (
    <div className="fifa-first-scorer">
      <label className="fifa-first-scorer-toggle">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(event) =>
            onChange(event.target.checked ? { ...emptyFirstScorer(), kind: "no_goal" } : emptyFirstScorer())
          }
        />
        <span>{label}</span>
      </label>
      {enabled ? (
        <div className="fifa-scorer-picker" ref={pickerRef}>
          <button
            className="input-secondary fifa-first-scorer-select"
            type="button"
            disabled={disabled}
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
          >
            <span>{selectedLabel}</span>
            <span aria-hidden="true">{pickerOpen ? "▲" : "▼"}</span>
          </button>
          {pickerOpen ? (
            <div className="fifa-scorer-menu">
              <input
                className="input-secondary fifa-scorer-search"
                value={search}
                autoFocus
                placeholder="Wpisz nazwisko lub imię"
                aria-label="Szukaj pierwszego strzelca"
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="fifa-scorer-options">
                {!normalizedSearch ? (
                  <button
                    className={scorer.kind === "no_goal" ? "fifa-scorer-option fifa-scorer-option--active" : "fifa-scorer-option"}
                    type="button"
                    onClick={() => selectScorer({ ...emptyFirstScorer(), kind: "no_goal" })}
                  >
                    Nikt nie strzeli (0:0)
                  </button>
                ) : null}
                {[
                  [match.homeTeam, filteredHomePlayers],
                  [match.awayTeam, filteredAwayPlayers],
                ].map(([team, players]) =>
                  players.length || !normalizedSearch ? (
                    <div className="fifa-scorer-team" key={team}>
                      <strong>{team}</strong>
                      {players.map((player) => (
                        <button
                          className={
                            scorer.kind === "player" && scorer.playerId === player.id
                              ? "fifa-scorer-option fifa-scorer-option--active"
                              : "fifa-scorer-option"
                          }
                          type="button"
                          key={player.id}
                          onClick={() =>
                            selectScorer({
                              kind: "player",
                              playerId: player.id,
                              playerName: player.fullName,
                              team,
                            })
                          }
                        >
                          {player.fullName}
                        </button>
                      ))}
                      {!normalizedSearch ? (
                        <button
                          className={
                            scorer.kind === "own_goal" && scorer.team === team
                              ? "fifa-scorer-option fifa-scorer-option--active"
                              : "fifa-scorer-option"
                          }
                          type="button"
                          onClick={() =>
                            selectScorer({
                              kind: "own_goal",
                              playerId: null,
                              playerName: null,
                              team,
                            })
                          }
                        >
                          Gol samobójczy dla {team}
                        </button>
                      ) : null}
                    </div>
                  ) : null
                )}
                {normalizedSearch &&
                !filteredHomePlayers.length &&
                !filteredAwayPlayers.length ? (
                  <p className="fifa-scorer-empty">Brak pasujących zawodników.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TournamentSpecialBetsPanel({
  room,
  playersByTeam,
  value,
  savedValue,
  saving,
  onChange,
  onSave,
  adminResult,
  onAdminResultChange,
  onSaveAdminResult,
}) {
  const rules = room?.scoringRules?.tournamentSpecials || {};
  if (!rules.enabled) return null;

  const teams = Object.keys(playersByTeam || {}).sort((a, b) =>
    a.localeCompare(b, "pl")
  );
  const players = teams.flatMap((team) =>
    (playersByTeam?.[team] || []).map((player) => ({
      ...player,
      team,
      searchLabel: `${player.fullName} (${team})`,
    }))
  );
  const locked = room?.specialBetsLocked === true;
  const selectedGoldenBoot = value?.goldenBoot || {};
  const adminGoldenBoot = adminResult?.goldenBoot || {};
  const hasInvalidGoldenBootText =
    Boolean(selectedGoldenBoot.playerName) && !selectedGoldenBoot.playerId;
  const currentKey = [
    value?.championTeam || "",
    selectedGoldenBoot.playerId || "",
  ].join("|");
  const savedKey = [
    savedValue?.championTeam || "",
    savedValue?.goldenBoot?.playerId || "",
  ].join("|");
  const hasSelection =
    Boolean(value?.championTeam) || Boolean(selectedGoldenBoot.playerId);
  const canSave =
    !locked &&
    !saving &&
    hasSelection &&
    !hasInvalidGoldenBootText &&
    currentKey !== savedKey;
  const adminResultValid =
    Boolean(adminResult?.championTeam) && Boolean(adminGoldenBoot.playerId);

  const updateGoldenBoot = (inputValue, target) => {
    const player = players.find(
      (item) =>
        item.searchLabel === inputValue ||
        item.id === inputValue
    );
    target(
      player
        ? {
            playerId: player.id,
            playerName: player.fullName,
            team: player.team,
          }
        : { playerId: null, playerName: inputValue, team: null }
    );
  };

  return (
    <div className="panel-card fifa-panel fifa-special-bets">
      <div className="fifa-special-bets-head">
        <div>
          <strong>Wytypuj mistrza świata i króla strzelców</strong>
          <p className="fifa-muted">
            {locked
              ? "Typowanie zostało zamknięte."
              : `Możesz wskazać oba typy albo tylko jeden. Zapisz przed ${formatDate(
                  room.specialBetsLockAt
                )}.`}
          </p>
        </div>
        <span className={locked ? "fifa-special-status is-locked" : "fifa-special-status"}>
          {locked ? "Zamknięte" : "Otwarte"}
        </span>
      </div>

      <div className="fifa-special-bets-grid">
        {rules.championEnabled !== false ? (
          <label className="fifa-field">
            <span className="fifa-label">Mistrz świata</span>
            <select
              className="input-secondary"
              value={value?.championTeam || ""}
              disabled={locked}
              onChange={(event) =>
                onChange({ ...value, championTeam: event.target.value })
              }
            >
              <option value="">Nie typuję</option>
              {teams.map((team) => (
                <option value={team} key={team}>
                  {team}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {rules.goldenBootEnabled !== false ? (
          <label className="fifa-field">
            <span className="fifa-label">Król strzelców</span>
            <input
              className="input-secondary"
              list="fifa-golden-boot-players"
              value={
                selectedGoldenBoot.playerName
                  ? `${selectedGoldenBoot.playerName}${
                      selectedGoldenBoot.team
                        ? ` (${selectedGoldenBoot.team})`
                        : ""
                    }`
                  : ""
              }
              disabled={locked}
              placeholder="Wpisz nazwisko zawodnika"
              onChange={(event) =>
                updateGoldenBoot(event.target.value, (goldenBoot) =>
                  onChange({ ...value, goldenBoot })
                )
              }
            />
            {hasInvalidGoldenBootText ? (
              <small className="fifa-field-error">
                Wybierz zawodnika z listy podpowiedzi.
              </small>
            ) : null}
          </label>
        ) : null}
      </div>

      <datalist id="fifa-golden-boot-players">
        {players.map((player) => (
          <option value={player.searchLabel} key={player.id} />
        ))}
      </datalist>

      <div className="fifa-special-actions">
        <button
          className="btn btn-primary"
          type="button"
          disabled={!canSave}
          onClick={onSave}
        >
          {saving ? "Zapisuję..." : "Zapisz zakłady turniejowe"}
        </button>
      </div>

      {savedValue?.championTeam || savedValue?.goldenBoot?.playerId ? (
        <div className="fifa-saved-pick fifa-saved-pick--special">
          <strong>Twój zapisany typ</strong>
          {savedValue?.championTeam ? (
            <span>Mistrz świata: {savedValue.championTeam}</span>
          ) : null}
          {savedValue?.goldenBoot?.playerId ? (
            <span>
              Król strzelców: {savedValue.goldenBoot.playerName}
              {savedValue.goldenBoot.team
                ? ` (${savedValue.goldenBoot.team})`
                : ""}
            </span>
          ) : null}
        </div>
      ) : null}

      {room?.specialResult?.status === "finished" ? (
        <div className="fifa-special-result-summary">
          <strong>Rozstrzygnięcie</strong>
          <span>Mistrz: {room.specialResult.championTeam}</span>
          <span>
            Król strzelców: {room.specialResult.goldenBoot?.playerName} (
            {room.specialResult.goldenBoot?.team})
          </span>
        </div>
      ) : null}

      {room?.canManage && room?.isSystemAdmin ? (
        <details className="fifa-special-admin">
          <summary>Rozstrzygnięcie administratora</summary>
          <div className="fifa-special-bets-grid">
            <label className="fifa-field">
              <span className="fifa-label">Mistrz świata</span>
              <select
                className="input-secondary"
                value={adminResult?.championTeam || ""}
                onChange={(event) =>
                  onAdminResultChange({
                    ...adminResult,
                    championTeam: event.target.value,
                  })
                }
              >
                <option value="">Wybierz reprezentację</option>
                {teams.map((team) => (
                  <option value={team} key={`admin-${team}`}>
                    {team}
                  </option>
                ))}
              </select>
            </label>
            <label className="fifa-field">
              <span className="fifa-label">Król strzelców</span>
              <input
                className="input-secondary"
                list="fifa-golden-boot-players"
                value={
                  adminGoldenBoot.playerName
                    ? `${adminGoldenBoot.playerName}${
                        adminGoldenBoot.team
                          ? ` (${adminGoldenBoot.team})`
                          : ""
                      }`
                    : ""
                }
                placeholder="Wpisz nazwisko zawodnika"
                onChange={(event) =>
                  updateGoldenBoot(event.target.value, (goldenBoot) =>
                    onAdminResultChange({ ...adminResult, goldenBoot })
                  )
                }
              />
            </label>
          </div>
          <button
            className="btn btn-secondary"
            type="button"
            disabled={!adminResultValid}
            onClick={onSaveAdminResult}
          >
            Zapisz rozstrzygnięcie
          </button>
        </details>
      ) : null}
    </div>
  );
}

function TournamentSpecialsUnavailablePanel({ room, saving, onEnable }) {
  return (
    <div className="panel-card fifa-panel fifa-special-bets fifa-special-bets--disabled">
      <div className="fifa-special-bets-head">
        <div>
          <strong>Wytypuj mistrza świata i króla strzelców</strong>
          <p className="fifa-muted">
            Ta liga nie ma włączonych zakładów turniejowych.
          </p>
        </div>
        {room?.canManage && !room?.specialBetsLocked ? (
          <button
            className="btn btn-secondary"
            type="button"
            disabled={saving}
            onClick={onEnable}
          >
            {saving ? "Włączam..." : "Włącz wariant 10/12"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function TournamentSpecialsLeaguePreview({ room, prediction, onOpenMain }) {
  const rules = room?.scoringRules?.tournamentSpecials || {};
  if (!rules.enabled) return null;

  return (
    <div className="panel-card fifa-panel fifa-special-bets fifa-special-league-preview">
      <div className="fifa-special-bets-head">
        <div>
          <strong>Twoje zakłady turniejowe</strong>
          <p className="fifa-muted">
            Typy ustawiasz w głównym typerze. W tej lidze są tylko podglądane i
            punktowane według jej zasad.
          </p>
        </div>
        <span
          className={
            room?.specialBetsLocked
              ? "fifa-special-status is-locked"
              : "fifa-special-status"
          }
        >
          {room?.specialBetsLocked ? "Zamknięte" : "Otwarte"}
        </span>
      </div>

      {prediction?.championTeam || prediction?.goldenBoot?.playerId ? (
        <div className="fifa-saved-pick fifa-saved-pick--special">
          <strong>Twój zapisany typ</strong>
          <span>
            Mistrz świata: {prediction?.championTeam || "bez typu"} ·{" "}
            {rules.championPoints ?? 10} pkt
          </span>
          <span>
            Król strzelców:{" "}
            {prediction?.goldenBoot?.playerId
              ? `${prediction.goldenBoot.playerName}${
                  prediction.goldenBoot.team
                    ? ` (${prediction.goldenBoot.team})`
                    : ""
                }`
              : "bez typu"}{" "}
            · {rules.goldenBootPoints ?? 12} pkt
          </span>
        </div>
      ) : (
        <p className="fifa-muted">
          Nie masz jeszcze zapisanego typu. Wróć do głównego typera, aby wskazać
          mistrza świata lub króla strzelców.
        </p>
      )}
      {!room?.specialBetsLocked ? (
        <div className="fifa-special-actions">
          <button className="btn btn-secondary" type="button" onClick={onOpenMain}>
            Przejdź do głównego typera
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MatchPrediction({
  room,
  match,
  prediction,
  draft,
  playersByTeam,
  isAdmin,
  onDraftChange,
  onSavePrediction,
  onSaved,
  onError,
  previewMode = false,
}) {
  const [resultHome, setResultHome] = useState(match.result?.homeGoals ?? "");
  const [resultAway, setResultAway] = useState(match.result?.awayGoals ?? "");
  const [resultFirstScorer, setResultFirstScorer] = useState(
    match.result?.firstScorer || emptyFirstScorer()
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setResultHome(match.result?.homeGoals ?? "");
    setResultAway(match.result?.awayGoals ?? "");
    setResultFirstScorer(match.result?.firstScorer || emptyFirstScorer());
  }, [match.result?.homeGoals, match.result?.awayGoals, match.result?.firstScorer]);

  const participantsKnown = hasKnownParticipants(match);
  const canSavePrediction =
    !previewMode &&
    (room?.isParticipant || room?.isPredictionWorkspace) &&
    participantsKnown &&
    !match.isLocked;
  const canEditPrediction =
    participantsKnown && !match.isLocked && (previewMode || canSavePrediction);
  const hasResult = match.result?.status === "finished";
  const predictionComplete = draft && draft.homeGoals !== "" && draft.awayGoals !== "";
  const predictionChanged =
    predictionComplete &&
    (!prediction ||
      Number(prediction.homeGoals) !== Number(draft.homeGoals) ||
      Number(prediction.awayGoals) !== Number(draft.awayGoals) ||
      firstScorerKey(prediction.firstScorer) !== firstScorerKey(draft.firstScorer));
  const resultComplete = resultHome !== "" && resultAway !== "";
  const resultChanged =
    resultComplete &&
    (match.result?.status !== "finished" ||
      Number(match.result?.homeGoals) !== Number(resultHome) ||
      Number(match.result?.awayGoals) !== Number(resultAway) ||
      firstScorerKey(match.result?.firstScorer) !== firstScorerKey(resultFirstScorer));
  const predictionValidationError = validateScorerForScore(
    draft?.firstScorer,
    draft?.homeGoals,
    draft?.awayGoals,
    match
  );
  const resultValidationError = validateScorerForScore(
    resultFirstScorer,
    resultHome,
    resultAway,
    match
  );

  const savePrediction = async () => {
    if (predictionValidationError) {
      onError(predictionValidationError);
      return;
    }
    setSaving(true);
    onError("");
    try {
      await onSavePrediction(match);
    } catch (err) {
      onError(fifaErrorMessage(err, "Nie udało się zapisać typu."));
    } finally {
      setSaving(false);
    }
  };

  const saveResult = async () => {
    if (resultValidationError) {
      onError(resultValidationError);
      return;
    }
    setSaving(true);
    onError("");
    try {
      const data = await saveFifaResult(room.code, match.id, {
        homeGoals: Number(resultHome),
        awayGoals: Number(resultAway),
        firstScorer: resultFirstScorer,
      });
      onSaved(data);
    } catch (err) {
      onError(fifaErrorMessage(err, "Nie udało się zapisać wyniku."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fifa-match-card">
      <div className="fifa-match">
        <div className="fifa-match-no">#{match.matchNo}</div>
        <div>
          <div className="fifa-teams">
            {match.homeTeam}{"\u00a0-"} {match.awayTeam}
          </div>
        </div>
        <div className="fifa-prediction-box">
          <div className="fifa-scoreline" aria-label="Typowany wynik">
            <ScoreInput
              ariaLabel={`${match.homeTeam} - typowana liczba goli`}
              value={draft?.homeGoals ?? ""}
              disabled={!canEditPrediction || saving}
              onChange={(value) => onDraftChange(match.id, { homeGoals: value })}
            />
            <span>:</span>
            <ScoreInput
              ariaLabel={`${match.awayTeam} - typowana liczba goli`}
              value={draft?.awayGoals ?? ""}
              disabled={!canEditPrediction || saving}
              onChange={(value) => onDraftChange(match.id, { awayGoals: value })}
            />
            <button
              className="btn btn-primary"
              type="button"
              disabled={!canSavePrediction || saving || !predictionChanged || !!predictionValidationError}
              onClick={savePrediction}
            >
              Zapisz
            </button>
          </div>
          <div className="fifa-status">
            {hasResult
              ? `Wynik: ${match.result.homeGoals}:${match.result.awayGoals}, punkty: ${prediction?.points ?? 0}`
              : !participantsKnown
                ? "Typowanie będzie dostępne po ustaleniu uczestników"
              : match.isLocked
                ? "Typowanie zamknięte"
                : previewMode
                  ? "Podgląd bez konta - typ nie zostanie zapisany"
                : predictionChanged
                  ? "Niezapisane zmiany"
                  : prediction
                  ? "Typ zapisany"
                  : "Brak typu"}
          </div>
          {prediction ? (
            <div className="fifa-saved-pick">
              <strong>Twój zapisany typ:</strong>
              <span>
                {prediction.homeGoals}:{prediction.awayGoals}
              </span>
              {firstScorerLabel(prediction.firstScorer) ? (
                <span>{firstScorerLabel(prediction.firstScorer)}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        {room?.isPredictionWorkspace || room?.scoringRules?.firstScorer?.enabled ? (
          <div className="fifa-prediction-scorer-field">
            <FirstScorerField
              value={draft?.firstScorer}
              match={match}
              playersByTeam={playersByTeam}
              disabled={!canEditPrediction || saving}
              onChange={(value) => onDraftChange(match.id, { firstScorer: value })}
            />
            {predictionValidationError ? (
              <div className="fifa-scorer-validation" role="alert" aria-live="polite">
                {predictionValidationError}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="fifa-match-meta-row">
          <span>{match.group ? groupLabel(match.group) : stageLabel(match.stage)}</span>
          <span>{formatDate(match.kickoffAt)}</span>
          <span>{match.venue}</span>
          <span>Typowanie do: {formatDate(match.lockAt)}</span>
        </div>
      </div>
      {isAdmin ? (
        <div className="fifa-admin-result">
          <span className="fifa-admin-label">Wynik rzeczywisty</span>
          <ScoreInput
            ariaLabel={`${match.homeTeam} - rzeczywista liczba goli`}
            value={resultHome}
            onChange={setResultHome}
          />
          <span>:</span>
          <ScoreInput
            ariaLabel={`${match.awayTeam} - rzeczywista liczba goli`}
            value={resultAway}
            onChange={setResultAway}
          />
          <button
            className="btn btn-secondary"
            type="button"
            disabled={saving || !resultChanged || !!resultValidationError}
            onClick={saveResult}
          >
            Wynik
          </button>
          <FirstScorerField
            value={resultFirstScorer}
            match={match}
            playersByTeam={playersByTeam}
            disabled={saving}
            label="Ustaw pierwszego strzelca"
            onChange={setResultFirstScorer}
          />
          {resultValidationError ? (
            <div className="fifa-scorer-validation" role="alert" aria-live="polite">
              {resultValidationError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LeagueDetailsPanel({ details, onClose, onHistoricalDecision, standalone = false }) {
  const [detailsSortMode, setDetailsSortMode] = useState("time");
  const [detailsMultiSelect, setDetailsMultiSelect] = useState(false);
  const [selectedDetailFilters, setSelectedDetailFilters] = useState(["all"]);
  const [detailsFilterInitialized, setDetailsFilterInitialized] = useState(false);
  const [detailsFiltersExpanded, setDetailsFiltersExpanded] = useState(false);
  const [expandedDetailMatches, setExpandedDetailMatches] = useState([]);
  const [historicalImportsOpen, setHistoricalImportsOpen] = useState(true);
  const participantRows = useMemo(() => {
    const rows = details?.participants?.length
      ? details.participants
      : details?.leaderboard || [];
    return [...rows].sort((a, b) => {
      const rankA =
        Number.isFinite(Number(a.rank)) && Number(a.rank) > 0
          ? Number(a.rank)
          : Number.POSITIVE_INFINITY;
      const rankB =
        Number.isFinite(Number(b.rank)) && Number(b.rank) > 0
          ? Number(b.rank)
          : Number.POSITIVE_INFINITY;
      return (
        rankA - rankB ||
        String(a.displayName || "").localeCompare(String(b.displayName || ""), "pl")
      );
    });
  }, [details?.leaderboard, details?.participants]);
  const detailMatches = details?.matches || [];
  const closeScoreEnabled = details?.room?.scoringRules?.closeScoreEnabled === true;
  const detailFilterOptions = useMemo(() => {
    const options = new Map();
    detailMatches.forEach((match) => {
      const key =
        detailsSortMode === "groups"
          ? match.group
            ? `group:${match.group}`
            : `stage:${match.stage}`
          : `day:${warsawDateKey(match.kickoffAt)}`;
      const label =
        detailsSortMode === "groups"
          ? match.group
            ? groupLabel(match.group)
            : stageLabel(match.stage)
          : new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium" }).format(
              new Date(match.kickoffAt)
            );
      if (!options.has(key)) options.set(key, label);
    });
    return [...options.entries()].map(([key, label]) => ({ key, label }));
  }, [detailMatches, detailsSortMode]);
  const visibleDetailMatches = useMemo(() => {
    if (selectedDetailFilters.includes("all")) return detailMatches;
    return detailMatches.filter((match) => {
      const key =
        detailsSortMode === "groups"
          ? match.group
            ? `group:${match.group}`
            : `stage:${match.stage}`
          : `day:${warsawDateKey(match.kickoffAt)}`;
      return selectedDetailFilters.includes(key);
    });
  }, [detailMatches, detailsSortMode, selectedDetailFilters]);

  useEffect(() => {
    if (detailsFilterInitialized || !detailFilterOptions.length) return;
    const today = warsawDateKey();
    const defaultOption =
      detailsSortMode === "time"
        ? detailFilterOptions.find((option) => option.key === `day:${today}`) ||
          detailFilterOptions.find((option) => option.key > `day:${today}`) ||
          detailFilterOptions[detailFilterOptions.length - 1]
        : detailFilterOptions.find((option) => option.key === "group:A") ||
          detailFilterOptions[0];
    if (defaultOption) {
      setSelectedDetailFilters([defaultOption.key]);
      setDetailsFilterInitialized(true);
    }
  }, [detailFilterOptions, detailsFilterInitialized, detailsSortMode]);

  const selectDetailFilter = (key) => {
    if (!detailsMultiSelect || key === "all") {
      setSelectedDetailFilters([key]);
      return;
    }
    setSelectedDetailFilters((current) => {
      const withoutAll = current.filter((item) => item !== "all");
      const next = withoutAll.includes(key)
        ? withoutAll.filter((item) => item !== key)
        : [...withoutAll, key];
      return next.length ? next : ["all"];
    });
  };

  const changeDetailsSortMode = (mode) => {
    setDetailsSortMode(mode);
    const today = warsawDateKey();
    const modeOptions = (() => {
      const options = new Map();
      detailMatches.forEach((match) => {
        const key =
          mode === "groups"
            ? match.group
              ? `group:${match.group}`
              : `stage:${match.stage}`
            : `day:${warsawDateKey(match.kickoffAt)}`;
        if (!options.has(key)) options.set(key, true);
      });
      return [...options.keys()];
    })();
    const defaultKey =
      mode === "groups"
        ? modeOptions.includes("group:A")
          ? "group:A"
          : modeOptions[0]
        : modeOptions.includes(`day:${today}`)
          ? `day:${today}`
          : modeOptions.find((key) => key > `day:${today}`) ||
            modeOptions[modeOptions.length - 1];
    setSelectedDetailFilters(defaultKey ? [defaultKey] : ["all"]);
    setDetailsFilterInitialized(true);
    setDetailsFiltersExpanded(false);
  };

  const toggleDetailMatch = (matchId) => {
    setExpandedDetailMatches((current) =>
      current.includes(matchId)
        ? current.filter((id) => id !== matchId)
        : [...current, matchId]
    );
  };

  if (!details) return null;

  return (
    <div className={standalone ? "panel-card fifa-panel fifa-details-panel fifa-details-panel--standalone" : "panel-card fifa-panel fifa-details-panel"}>
      {!standalone ? (
        <div className="fifa-collapsible-head">
          <strong>Szczegóły ligi</strong>
          <button className="btn btn-secondary fifa-icon-btn" type="button" onClick={onClose} aria-label="Zamknij szczegóły ligi">
            -
          </button>
        </div>
      ) : null}

      <div className="fifa-details-grid">
        <section>
          <h3 className="fifa-details-title">Tabela graczy</h3>
          {details.room?.canManage ? (
            historicalImportsOpen ? (
              <div className="fifa-import-requests">
                <div className="fifa-import-requests-head">
                  <strong>Typy sprzed dołączenia</strong>
                  <div className="fifa-import-requests-actions">
                    <span className="fifa-import-count">
                      {details.pendingHistoricalImports?.length || 0} do akceptacji
                    </span>
                    <button
                      className="fifa-import-close"
                      type="button"
                      aria-label="Ukryj typy sprzed dołączenia"
                      title="Ukryj"
                      onClick={() => setHistoricalImportsOpen(false)}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className="fifa-muted">
                  Gdy gracz dołączy po zamknięciu typowania, tutaj zdecydujesz,
                  czy jego wcześniej zapisane typy mają liczyć się w tej lidze.
                </p>
                {details.pendingHistoricalImports?.length ? (
                  details.pendingHistoricalImports.map((participant) => (
                    <div className="fifa-import-request" key={participant.userId}>
                      <div className="fifa-import-player">
                        <strong title={participant.displayName}>
                          {participant.displayName}
                        </strong>
                        <span className="fifa-muted">
                          Dołączył po zamknięciu części typów
                        </span>
                      </div>
                      <div>
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={() =>
                            onHistoricalDecision(participant.userId, true)
                          }
                        >
                          Zatwierdź
                        </button>
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() =>
                            onHistoricalDecision(participant.userId, false)
                          }
                        >
                          Odrzuć
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="fifa-import-empty">
                    Brak oczekujących próśb o doliczenie wcześniejszych typów.
                  </p>
                )}
              </div>
            ) : (
              <button
                className="fifa-import-reopen"
                type="button"
                onClick={() => setHistoricalImportsOpen(true)}
              >
                Typy sprzed dołączenia
                <span>{details.pendingHistoricalImports?.length || 0}</span>
              </button>
            )
          ) : null}
          <div className="fifa-details-table">
            {participantRows.length ? (
              participantRows.map((row) => (
                <div className="fifa-details-row fifa-player-row" key={row.userId}>
                  <span className="fifa-player-name" title={row.displayName}>
                    {row.displayName}
                  </span>
                  <div className="fifa-player-stats">
                    <span>{row.points} pkt</span>
                    <span>{rankLabel(row.rank, row.exAequo)}</span>
                    <span>{typyLabel(row.predictionsCount || 0)}</span>
                    {Number(row.specialPoints || 0) > 0 ? (
                      <span>w tym {row.specialPoints} pkt specjalnych</span>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <p className="fifa-muted">W tej lidze nie ma jeszcze graczy.</p>
            )}
          </div>
          {details.room?.scoringRules?.tournamentSpecials?.enabled ? (
            <div className="fifa-details-specials">
              <h3 className="fifa-details-title">Zakłady turniejowe</h3>
              {details.specialPredictions?.length ? (
                details.specialPredictions.map((prediction) => (
                  <div
                    className="fifa-details-special-row"
                    key={prediction.userId}
                  >
                    <strong title={prediction.displayName}>
                      {prediction.displayName}
                    </strong>
                    <span>
                      Mistrz: {prediction.championTeam || "bez typu"}
                    </span>
                    <span>
                      Król strzelców:{" "}
                      {prediction.goldenBoot?.playerName
                        ? `${prediction.goldenBoot.playerName} (${prediction.goldenBoot.team})`
                        : "bez typu"}
                    </span>
                    <span>
                      {prediction.points == null
                        ? "Oczekuje na rozstrzygnięcie"
                        : `${prediction.points} pkt`}
                    </span>
                  </div>
                ))
              ) : (
                <p className="fifa-muted">
                  Typy innych graczy pojawią się po zamknięciu zakładów
                  turniejowych.
                </p>
              )}
            </div>
          ) : null}
        </section>

        <section>
          <h3 className="fifa-details-title">Typy meczowe</h3>
          <div className="fifa-prediction-legend" aria-label="Legenda oceny typów">
            <span className="fifa-legend-item fifa-legend-item--exact">Dokładny wynik</span>
            {closeScoreEnabled ? (
              <span className="fifa-legend-item fifa-legend-item--close">Bliski wynik</span>
            ) : null}
            <span className="fifa-legend-item fifa-legend-item--outcome">Trafiony rezultat</span>
            <span className="fifa-legend-item fifa-legend-item--wrong">Nietrafiony typ</span>
          </div>
          <div className="fifa-details-organizer">
            <div className="fifa-details-organizer-head">
              <div className="fifa-segmented" role="group" aria-label="Układ typów meczowych">
                <button
                  className={detailsSortMode === "groups" ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => changeDetailsSortMode("groups")}
                >
                  Grupy
                </button>
                <button
                  className={detailsSortMode === "time" ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => changeDetailsSortMode("time")}
                >
                  Chronologicznie
                </button>
              </div>
              <label className="fifa-multi-toggle">
                <input
                  type="checkbox"
                  checked={detailsMultiSelect}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setDetailsMultiSelect(enabled);
                    if (!enabled) {
                      setSelectedDetailFilters((current) => [
                        current.find((item) => item !== "all") || "all",
                      ]);
                    }
                  }}
                />
                <span>Zaznaczaj wiele</span>
              </label>
            </div>
            <div
              className={`fifa-details-filter-grid${
                detailsFiltersExpanded ? " fifa-details-filter-grid--expanded" : ""
              }`}
            >
              {detailFilterOptions.map((option) => (
                <button
                  key={option.key}
                  className={
                    selectedDetailFilters.includes(option.key)
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  type="button"
                  onClick={() => selectDetailFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
              <button
                className={
                  selectedDetailFilters.includes("all") ? "btn btn-primary" : "btn btn-secondary"
                }
                type="button"
                onClick={() => selectDetailFilter("all")}
              >
                {detailsSortMode === "groups" ? "Wszystkie grupy i etapy" : "Wszystkie dni"}
              </button>
            </div>
            <button
              className="btn btn-secondary fifa-details-filter-toggle"
              type="button"
              aria-expanded={detailsFiltersExpanded}
              onClick={() => setDetailsFiltersExpanded((expanded) => !expanded)}
            >
              {detailsFiltersExpanded
                ? "Zwiń"
                : detailsSortMode === "groups"
                  ? "Pokaż wszystkie grupy i etapy"
                  : "Pokaż wszystkie daty"}
            </button>
          </div>
          <div className="fifa-details-match-list">
            {visibleDetailMatches.length ? visibleDetailMatches.map((match) => (
              <div className="fifa-details-match" key={match.id}>
                <button
                  className="fifa-details-match-head"
                  type="button"
                  aria-expanded={expandedDetailMatches.includes(match.id)}
                  onClick={() => toggleDetailMatch(match.id)}
                >
                  <span>
                    <strong>
                      #{match.matchNo} {match.homeTeam} - {match.awayTeam}
                    </strong>
                    <small>
                      {match.result?.status === "finished"
                        ? `Wynik ${match.result.homeGoals}:${match.result.awayGoals}`
                        : match.isLocked
                          ? "Czeka na wynik"
                          : "Typowanie otwarte"}
                    </small>
                    {actualFirstScorerLabel(match) ? (
                      <small className="fifa-details-first-scorer">
                        {actualFirstScorerLabel(match)}
                      </small>
                    ) : null}
                  </span>
                  <span className="fifa-match-expand-icon" aria-hidden="true">
                    {expandedDetailMatches.includes(match.id) ? "−" : "+"}
                  </span>
                </button>
                {expandedDetailMatches.includes(match.id) && match.revealPredictions ? (
                  match.predictions?.length ? (
                    <div className="fifa-details-table">
                      {match.predictions.map((prediction) => (
                        <div
                          className={`fifa-details-row fifa-prediction-row fifa-prediction-row--${prediction.grade || "pending"}`}
                          key={`${match.id}-${prediction.userId}`}
                        >
                          <span className="fifa-prediction-player" title={prediction.displayName}>
                            {prediction.displayName}
                          </span>
                          <strong className="fifa-prediction-score">
                            {prediction.homeGoals}:{prediction.awayGoals}
                          </strong>
                          {firstScorerLabel(prediction.firstScorer) ? (
                            <span className="fifa-prediction-scorer">
                              {firstScorerLabel(prediction.firstScorer)}
                            </span>
                          ) : null}
                          <div className="fifa-prediction-summary">
                            <span className="fifa-prediction-grade">
                              {prediction.grade === "exact"
                                ? "Dokładny"
                                : prediction.grade === "close"
                                  ? "Bliski wynik"
                                  : prediction.grade === "outcome"
                                    ? "Rezultat"
                                    : prediction.grade === "wrong"
                                      ? "Nietrafiony"
                                      : "Czeka na rozstrzygnięcie"}
                            </span>
                            <span>{prediction.points == null ? "-" : prediction.points} pkt</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="fifa-muted">Nikt jeszcze nie zapisał typu dla tego meczu.</p>
                  )
                ) : expandedDetailMatches.includes(match.id) ? (
                  <p className="fifa-muted">Typy będą widoczne po zamknięciu typowania.</p>
                ) : null}
              </div>
            )) : (
              <p className="fifa-muted">Brak meczów dla wybranych filtrów.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function FifaPage({ guestMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { leagueCode } = useParams();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [myRooms, setMyRooms] = useState([]);
  const [code, setCode] = useState("");
  const [newTitle, setNewTitle] = useState("Moja liga");
  const [exactScore, setExactScore] = useState(3);
  const [closeScoreEnabled, setCloseScoreEnabled] = useState(false);
  const [closeScore, setCloseScore] = useState(1.5);
  const [correctOutcome, setCorrectOutcome] = useState(1);
  const [phaseScoringMode, setPhaseScoringMode] = useState("flat");
  const [phaseExactIncrement, setPhaseExactIncrement] = useState(1);
  const [phaseCloseIncrement, setPhaseCloseIncrement] = useState(0.5);
  const [phaseOutcomeIncrement, setPhaseOutcomeIncrement] = useState(0.5);
  const [manualPhasePoints, setManualPhasePoints] = useState(
    createManualPhasePoints
  );
  const [firstScorerEnabled, setFirstScorerEnabled] = useState(false);
  const [firstScorerPlayerPoints, setFirstScorerPlayerPoints] = useState(3);
  const [firstScorerTeamPoints, setFirstScorerTeamPoints] = useState(1);
  const [firstScorerProgressionEnabled, setFirstScorerProgressionEnabled] =
    useState(false);
  const [firstScorerPlayerIncrement, setFirstScorerPlayerIncrement] = useState(1);
  const [firstScorerTeamIncrement, setFirstScorerTeamIncrement] = useState(0.5);
  const [tournamentSpecialsEnabled, setTournamentSpecialsEnabled] =
    useState(false);
  const [championPoints, setChampionPoints] = useState(10);
  const [goldenBootPoints, setGoldenBootPoints] = useState(12);
  const [specialDraft, setSpecialDraft] = useState({
    championTeam: "",
    goldenBoot: { playerId: null, playerName: "", team: null },
  });
  const [savingSpecial, setSavingSpecial] = useState(false);
  const [enablingSpecials, setEnablingSpecials] = useState(false);
  const [specialAdminResult, setSpecialAdminResult] = useState({
    championTeam: "",
    goldenBoot: { playerId: null, playerName: "", team: null },
  });
  const [appliedScoringPreset, setAppliedScoringPreset] = useState("");
  const [sortMode, setSortMode] = useState("time");
  const [mainMultiSelect, setMainMultiSelect] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("A");
  const [selectedKnockoutStage, setSelectedKnockoutStage] = useState("all");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedKnockoutStages, setSelectedKnockoutStages] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [dayListExpanded, setDayListExpanded] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [createLeagueOpen, setCreateLeagueOpen] = useState(false);
  const [createdCode, setCreatedCode] = useState("");
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [leagueDetailsOpen, setLeagueDetailsOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [joinNotice, setJoinNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsTitle, setDetailsTitle] = useState("");
  const [detailsTitleEditing, setDetailsTitleEditing] = useState(false);
  const [detailsTitleSaving, setDetailsTitleSaving] = useState(false);
  const detailsMode = Boolean(leagueCode);

  const buildGuestPreviewData = (sourceMatches = FIFA_WORLD_CUP_2026_MATCHES) => {
    const lockOffsetMs = 5 * 60 * 1000;
    const matches = sourceMatches.map((match) => {
      const kickoffAt = new Date(match.kickoffAt);
      const lockAt = match.lockAt
        ? new Date(match.lockAt)
        : new Date(kickoffAt.getTime() - lockOffsetMs);
      return {
        ...match,
        id: String(match.id || match.matchNo),
        kickoffAt: kickoffAt.toISOString(),
        lockAt: lockAt.toISOString(),
        result: match.result || {},
        isLocked: lockAt.getTime() <= Date.now(),
      };
    });

    return {
      ok: true,
      room: {
        title: "FIFA World Cup 2026",
        code: "",
        isParticipant: false,
        isPredictionWorkspace: true,
        scoringRules: {
          exactScore: 3,
          closeScoreEnabled: false,
          closeScore: 1.5,
          correctOutcome: 1,
        },
      },
      scheduleMeta: {
        matchesCount: matches.length,
        officialMatchesCount: matches.length,
        expectedMatchesCount: 104,
        expectedGroupMatchesCount: 72,
      },
      matches,
      playersByTeam: {},
      predictions: {},
      leaderboard: [],
      me: { isAdmin: false },
    };
  };

  useEffect(() => {
    const inviteNotice = location.state?.fifaInviteNotice;
    if (!inviteNotice) return;
    setNotice({ type: "success", text: inviteNotice });
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    let alive = true;
    if (guestMode) {
      setLoading(true);
      setError("");
      loadPublicFifaPreview()
        .then((payload) => {
          if (!alive) return;
          setData(buildGuestPreviewData(payload?.matches || FIFA_WORLD_CUP_2026_MATCHES));
          setMyRooms([]);
        })
        .catch(() => {
          if (!alive) return;
          setData(buildGuestPreviewData());
          setError("");
          setMyRooms([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
      return () => {
        alive = false;
      };
    }
    if (detailsMode) {
      setLoading(true);
      setError("");
      const normalizedCode = String(leagueCode || "").toUpperCase();
      Promise.all([
        loadFifaLeagueDetails(normalizedCode),
        loadMyFifaRooms().catch(() => ({ rooms: [] })),
      ])
        .then(([detailsPayload, roomsPayload]) => {
          if (!alive) return;
          setLeagueDetails(detailsPayload);
          setDetailsTitle(detailsPayload.room?.title || "");
          setDetailsTitleEditing(false);
          setLeagueDetailsOpen(true);
          setData({
            ok: true,
            room: {
              ...(detailsPayload.room || {}),
              code: detailsPayload.room?.code || normalizedCode,
              isParticipant: true,
            },
            matches: detailsPayload.matches || [],
            leaderboard: detailsPayload.leaderboard || [],
            predictions: {},
            me: {},
            scheduleMeta: {
              matchesCount: detailsPayload.matches?.length || 0,
              expectedMatchesCount: 104,
              expectedGroupMatchesCount: 72,
            },
          });
          setCode(detailsPayload.room?.code || normalizedCode);
          setMyRooms(roomsPayload?.rooms || []);
        })
        .catch((err) => alive && setError(err.message || "Nie udalo sie zaladowac szczegolow ligi."))
        .finally(() => alive && setLoading(false));
      return () => {
        alive = false;
      };
    }

    Promise.all([loadDefaultFifaRoom(), loadMyFifaRooms().catch(() => ({ rooms: [] }))])
      .then(([payload, roomsPayload]) => {
        if (!alive) return;
        setData(payload);
        setCode("");
        setMyRooms(roomsPayload?.rooms || []);
      })
      .catch((err) => alive && setError(err.message || "Nie udało się załadować typera."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [detailsMode, guestMode, leagueCode]);

  const matches = data?.matches || [];
  const room = data?.room || null;
  const leaderboard = data?.leaderboard || [];
  const me = data?.me || {};
  const hasActiveLeague = !!room?.isParticipant;
  const canPredict =
    !guestMode && (hasActiveLeague || !!room?.isPredictionWorkspace);
  const myStanding = useMemo(() => getMyStanding(data), [data]);
  const myPoints = myStanding?.points || 0;
  const myRankLabel = rankLabel(myStanding?.rank, myStanding?.exAequo);
  const leaguePreviewRows = useMemo(() => {
    if (leaderboard.length) return leaderboard.slice(0, 5);
    return [...(room?.participants || [])]
      .sort((a, b) => String(a.displayName || "").localeCompare(String(b.displayName || ""), "pl"))
      .slice(0, 5)
      .map((participant) => ({
        userId: participant.userId,
        displayName: participant.displayName,
        rank: null,
        points: 0,
      }));
  }, [leaderboard, room?.participants]);

  useEffect(() => {
    if (detailsMode) return;
    setLeagueDetails(null);
    setLeagueDetailsOpen(false);
  }, [detailsMode, room?.code]);

  useEffect(() => {
    if (detailsMode || !room?.code) return undefined;
    const timer = setInterval(async () => {
      try {
        setData(await loadFifaRoom(room.code));
      } catch {
        // The server remains the source of truth for locked predictions.
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [detailsMode, room?.code]);

  useEffect(() => {
    if (!detailsMode || !leagueCode) return undefined;
    const normalizedCode = String(leagueCode).toUpperCase();
    const timer = setInterval(async () => {
      try {
        const payload = await loadFifaLeagueDetails(normalizedCode);
        setLeagueDetails(payload);
        if (!detailsTitleEditing) setDetailsTitle(payload.room?.title || "");
        setData((current) => ({
          ...(current || {}),
          room: {
            ...(current?.room || {}),
            ...(payload.room || {}),
            code: payload.room?.code || normalizedCode,
            isParticipant: true,
          },
          matches: payload.matches || [],
          leaderboard: payload.leaderboard || [],
        }));
      } catch {
        // Keep the current snapshot; the next refresh will try again.
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [detailsMode, detailsTitleEditing, leagueCode]);

  useEffect(() => {
    const next = {};
    Object.entries(data?.predictions || {}).forEach(([matchId, prediction]) => {
      next[matchId] = {
        homeGoals: prediction?.homeGoals ?? "",
        awayGoals: prediction?.awayGoals ?? "",
        firstScorer: prediction?.firstScorer || emptyFirstScorer(),
      };
    });
    setDrafts((prev) => ({ ...next, ...prev }));
  }, [data?.predictions]);

  useEffect(() => {
    const prediction = data?.specialPrediction;
    setSpecialDraft({
      championTeam: prediction?.championTeam || "",
      goldenBoot: prediction?.goldenBoot || {
        playerId: null,
        playerName: "",
        team: null,
      },
      championPoints: prediction?.championPoints ?? null,
      goldenBootPoints: prediction?.goldenBootPoints ?? null,
      points: prediction?.points ?? null,
    });
    const result = data?.room?.specialResult;
    setSpecialAdminResult({
      championTeam: result?.championTeam || "",
      goldenBoot: result?.goldenBoot || {
        playerId: null,
        playerName: "",
        team: null,
      },
    });
  }, [data?.room?.specialResult, data?.specialPrediction]);

  const groupedMatches = useMemo(() => {
    const buckets = matches.reduce((acc, match) => {
      const key = match.group ? groupLabel(match.group) : stageLabel(match.stage);
      if (!acc[key]) acc[key] = [];
      acc[key].push(match);
      return acc;
    }, {});
    Object.values(buckets).forEach((groupMatches) => {
      groupMatches.sort((a, b) => a.matchNo - b.matchNo);
    });
    const groups = [...new Set(matches.map((match) => match.group).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, "pl")
    );
    const sectionOrder = [
      ...groups.map(groupLabel),
      ...KNOCKOUT_STAGE_ORDER.map(stageLabel),
    ];
    return Object.fromEntries(
      Object.entries(buckets).sort(
        ([a], [b]) =>
          sectionOrder.indexOf(a) - sectionOrder.indexOf(b) ||
          a.localeCompare(b, "pl", { numeric: true })
      )
    );
  }, [matches]);

  const chronologicalMatches = useMemo(
    () => [...matches].sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt) || a.matchNo - b.matchNo),
    [matches]
  );

  const groupOptions = useMemo(
    () => [...new Set(matches.map((match) => match.group).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pl")),
    [matches]
  );

  const dayOptions = useMemo(() => {
    const byDay = new Map();
    chronologicalMatches.forEach((match) => {
      const key = warsawDateKey(match.kickoffAt);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(match);
    });
    return [...byDay.entries()].map(([dateKey, dayMatches]) => ({
      dateKey,
      label: new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium" }).format(new Date(`${dateKey}T12:00:00.000Z`)),
      count: dayMatches.length,
    }));
  }, [chronologicalMatches]);

  useEffect(() => {
    if (!selectedDay && dayOptions[0]?.dateKey) {
      const today = warsawDateKey();
      const todayOption = dayOptions.find((day) => day.dateKey === today);
      const nextOption = dayOptions.find((day) => day.dateKey > today);
      setSelectedDay(todayOption?.dateKey || nextOption?.dateKey || dayOptions[dayOptions.length - 1].dateKey);
    }
  }, [dayOptions, selectedDay]);

  const compactDayOptions = useMemo(() => {
    if (dayListExpanded) return dayOptions;
    const today = warsawDateKey();
    const nearestIndex = dayOptions.findIndex((day) => day.dateKey >= today);
    const anchorIndex = nearestIndex >= 0 ? nearestIndex : Math.max(0, dayOptions.length - 10);
    return dayOptions.slice(anchorIndex, anchorIndex + 10);
  }, [dayListExpanded, dayOptions]);

  const visibleGroupedMatches = useMemo(() => {
    if (mainMultiSelect) {
      if (!selectedGroups.length) return groupedMatches;
      return Object.fromEntries(
        Object.entries(groupedMatches).filter(([key]) => {
          const selectedRegularGroup = selectedGroups.some(
            (group) => group !== "knockout" && groupLabel(group) === key
          );
          const selectedKnockout =
            selectedGroups.includes("knockout") &&
            KNOCKOUT_STAGE_ORDER.some(
              (stage) =>
                stageLabel(stage) === key &&
                (!selectedKnockoutStages.length || selectedKnockoutStages.includes(stage))
            );
          return selectedRegularGroup || selectedKnockout;
        })
      );
    }
    if (selectedGroup === "all") return groupedMatches;
    if (selectedGroup === "knockout") {
      return Object.fromEntries(
        Object.entries(groupedMatches).filter(([key]) =>
          KNOCKOUT_STAGE_ORDER.some(
            (stage) =>
              stageLabel(stage) === key &&
              (selectedKnockoutStage === "all" || stage === selectedKnockoutStage)
          )
        )
      );
    }
    const key = groupLabel(selectedGroup);
    return groupedMatches[key] ? { [key]: groupedMatches[key] } : {};
  }, [
    groupedMatches,
    mainMultiSelect,
    selectedGroup,
    selectedGroups,
    selectedKnockoutStage,
    selectedKnockoutStages,
  ]);

  const visibleChronologicalMatches = useMemo(() => {
    if (mainMultiSelect) {
      if (!selectedDays.length) return chronologicalMatches;
      return chronologicalMatches.filter((match) =>
        selectedDays.includes(warsawDateKey(match.kickoffAt))
      );
    }
    if (selectedDay === "all") return chronologicalMatches;
    return chronologicalMatches.filter((match) => warsawDateKey(match.kickoffAt) === selectedDay);
  }, [chronologicalMatches, mainMultiSelect, selectedDay, selectedDays]);

  const toggleMainMultiSelect = () => {
    setMainMultiSelect((enabled) => {
      const next = !enabled;
      if (next) {
        setSelectedDays(selectedDay && selectedDay !== "all" ? [selectedDay] : []);
        setSelectedGroups(
          selectedGroup && selectedGroup !== "all" ? [selectedGroup] : []
        );
        setSelectedKnockoutStages(
          selectedKnockoutStage !== "all" ? [selectedKnockoutStage] : []
        );
        setDayListExpanded(false);
      } else {
        setSelectedDay(selectedDays[0] || "all");
        setSelectedGroup(selectedGroups[0] || "all");
        setSelectedKnockoutStage(selectedKnockoutStages[0] || "all");
      }
      return next;
    });
  };

  const toggleSelectedValue = (setter, value) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const currentVisibleMatches = useMemo(
    () =>
      sortMode === "groups"
        ? Object.values(visibleGroupedMatches).flat()
        : visibleChronologicalMatches,
    [sortMode, visibleChronologicalMatches, visibleGroupedMatches]
  );

  const hasSavableDrafts = useMemo(
    () =>
      matches.some((match) => {
        if (match.isLocked || !hasKnownParticipants(match)) return false;
        const draft = drafts[match.id];
        if (!draft || draft.homeGoals === "" || draft.awayGoals === "") return false;
        const existing = data?.predictions?.[match.id];
        return (
          !existing ||
          Number(existing.homeGoals) !== Number(draft.homeGoals) ||
          Number(existing.awayGoals) !== Number(draft.awayGoals) ||
          firstScorerKey(existing.firstScorer) !== firstScorerKey(draft.firstScorer)
        );
      }),
    [data?.predictions, drafts, matches]
  );

  const updateDraft = (matchId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [matchId]: {
        homeGoals: prev[matchId]?.homeGoals ?? "",
        awayGoals: prev[matchId]?.awayGoals ?? "",
        firstScorer: prev[matchId]?.firstScorer || emptyFirstScorer(),
        ...patch,
      },
    }));
  };

  const saveOnePrediction = async (match) => {
    if (guestMode) {
      setNotice({ type: "info", text: "Załóż konto, aby zapisywać typy." });
      return data;
    }
    const draft = drafts[match.id] || {};
    const payload = {
      homeGoals: Number(draft.homeGoals),
      awayGoals: Number(draft.awayGoals),
      firstScorer: draft.firstScorer || emptyFirstScorer(),
    };
    const next = await saveFifaPrediction(room.code, match.id, payload);
    setData(next);
    const roomsPayload = await loadMyFifaRooms().catch(() => ({ rooms: [] }));
    setMyRooms(roomsPayload?.rooms || []);
    return next;
  };

  const saveAllPredictions = async () => {
    if (!canPredict || !hasSavableDrafts) return;
    setSavingAll(true);
    setError("");
    try {
      let latest = data;
      for (const match of matches) {
        const draft = drafts[match.id];
        if (
          !draft ||
          match.isLocked ||
          !hasKnownParticipants(match) ||
          draft.homeGoals === "" ||
          draft.awayGoals === ""
        ) {
          continue;
        }
        const existing = data?.predictions?.[match.id];
        const unchanged =
          existing &&
          Number(existing.homeGoals) === Number(draft.homeGoals) &&
          Number(existing.awayGoals) === Number(draft.awayGoals) &&
          firstScorerKey(existing.firstScorer) === firstScorerKey(draft.firstScorer);
        if (unchanged) continue;
        const validationError = validateScorerForScore(
          draft.firstScorer,
          draft.homeGoals,
          draft.awayGoals,
          match
        );
        if (validationError) throw new Error(validationError);
        latest = await saveFifaPrediction(room.code, match.id, {
          homeGoals: Number(draft.homeGoals),
          awayGoals: Number(draft.awayGoals),
          firstScorer: draft.firstScorer || emptyFirstScorer(),
        });
      }
      setData(latest);
      const roomsPayload = await loadMyFifaRooms().catch(() => ({ rooms: [] }));
      setMyRooms(roomsPayload?.rooms || []);
    } catch (err) {
      setError(fifaErrorMessage(err, "Nie udało się zapisać wszystkich typów."));
    } finally {
      setSavingAll(false);
    }
  };

  const saveSpecialPrediction = async () => {
    if (!room?.code || room?.specialBetsLocked) return;
    setSavingSpecial(true);
    setError("");
    try {
      const payload = await saveFifaSpecialPrediction(room.code, {
        championTeam: specialDraft.championTeam || null,
        goldenBoot: specialDraft.goldenBoot,
      });
      setData(payload);
      const roomsPayload = await loadMyFifaRooms().catch(() => ({ rooms: [] }));
      setMyRooms(roomsPayload?.rooms || []);
      setNotice({
        type: "success",
        text: "Zakłady turniejowe zostały zapisane.",
      });
    } catch (err) {
      setError(
        fifaErrorMessage(err, "Nie udało się zapisać zakładów turniejowych.")
      );
    } finally {
      setSavingSpecial(false);
    }
  };

  const saveSpecialResult = async () => {
    setError("");
    try {
      await saveFifaSpecialResult(specialAdminResult);
      const payload = await loadFifaRoom(room.code);
      setData(payload);
      setNotice({
        type: "success",
        text: "Rozstrzygnięcie zakładów turniejowych zostało zapisane.",
      });
    } catch (err) {
      setError(
        fifaErrorMessage(err, "Nie udało się zapisać rozstrzygnięcia.")
      );
    }
  };

  const openPredictionWorkspace = async () => {
    setError("");
    try {
      const payload = await loadDefaultFifaRoom();
      setData(payload);
      setCode("");
    } catch (err) {
      setError(
        fifaErrorMessage(err, "Nie udało się otworzyć głównego typera.")
      );
    }
  };

  const enableTournamentSpecials = async () => {
    if (!room?.code || room?.specialBetsLocked) return;
    setEnablingSpecials(true);
    setError("");
    try {
      const payload = await updateFifaTournamentSpecials(room.code, {
        enabled: true,
        championPoints: 10,
        goldenBootPoints: 12,
      });
      setData(payload);
      setNotice({
        type: "success",
        text: "Włączono zakłady turniejowe: 10 pkt za mistrza i 12 pkt za króla strzelców.",
      });
    } catch (err) {
      setError(
        fifaErrorMessage(err, "Nie udało się włączyć zakładów turniejowych.")
      );
    } finally {
      setEnablingSpecials(false);
    }
  };

  const joinRoom = async () => {
    if (guestMode) {
      setJoinNotice({ type: "info", text: "Załóż konto, aby dołączyć do ligi." });
      return;
    }
    setError("");
    setJoinNotice(null);
    const cleanCode = String(code || "").trim().toUpperCase();
    if (!cleanCode) {
      setJoinNotice({ type: "error", text: "Wpisz kod ligi." });
      return;
    }
    try {
      const payload = await joinFifaRoom(cleanCode);
      setData(payload);
      const roomsPayload = await loadMyFifaRooms().catch(() => ({ rooms: [] }));
      setMyRooms(roomsPayload?.rooms || []);
      setJoinNotice({
        type: payload.alreadyParticipant ? "info" : "success",
        text: payload.alreadyParticipant
          ? `Już należysz do ligi "${payload.room?.title || cleanCode}".`
          : payload.historicalApprovalPending
            ? `Dołączono do ligi "${payload.room?.title || cleanCode}". Wcześniejsze typy czekają na zgodę administratora ligi.`
            : `Dołączono do ligi "${payload.room?.title || cleanCode}".`,
      });
    } catch (err) {
      setJoinNotice({
        type: "error",
        text: fifaErrorMessage(err, "Nie udało się dołączyć do ligi."),
      });
    }
  };

  const createRoom = async () => {
    if (guestMode) {
      setNotice({ type: "info", text: "Załóż konto, aby utworzyć ligę." });
      return;
    }
    setError("");
    setNotice(null);
    setCreatingRoom(true);
    try {
      const payload = await createFifaRoom({
        title: newTitle,
        scoringRules: {
          exactScore: Number(exactScore),
          closeScoreEnabled,
          closeScore: Number(closeScore),
          correctOutcome: Number(correctOutcome),
          phaseProgression: {
            mode: phaseScoringMode,
            enabled: phaseScoringMode === "progressive",
            exactIncrement: Number(phaseExactIncrement),
            closeIncrement: Number(phaseCloseIncrement),
            outcomeIncrement: Number(phaseOutcomeIncrement),
            manualPoints: manualPhasePoints,
          },
          firstScorer: {
            enabled: firstScorerEnabled,
            playerPoints: Number(firstScorerPlayerPoints),
            teamPoints: Number(firstScorerTeamPoints),
            phaseProgression: {
              enabled: firstScorerProgressionEnabled,
              playerIncrement: Number(firstScorerPlayerIncrement),
              teamIncrement: Number(firstScorerTeamIncrement),
            },
          },
          tournamentSpecials: {
            enabled: tournamentSpecialsEnabled,
            championEnabled: true,
            championPoints: Number(championPoints),
            goldenBootEnabled: true,
            goldenBootPoints: Number(goldenBootPoints),
          },
        },
      });
      setData(payload);
      setCode(payload.room?.code || "");
      setCreatedCode(payload.room?.code || "");
      const roomsPayload = await loadMyFifaRooms().catch(() => ({ rooms: [] }));
      setMyRooms(roomsPayload?.rooms || []);
      setNotice({
        type: "success",
        text: `Utworzono ligę "${payload.room?.title || newTitle}". Kod do zaproszenia: ${payload.room?.code || ""}.`,
      });
    } catch (err) {
      setError(fifaErrorMessage(err, "Nie udało się utworzyć ligi."));
    } finally {
      setCreatingRoom(false);
    }
  };

  const applyScoringPreset = (preset) => {
    setExactScore(3);
    setCorrectOutcome(1);
    setCloseScoreEnabled(preset.closeScoreEnabled);
    setCloseScore(1.5);
    setPhaseScoringMode("progressive");
    setPhaseExactIncrement(1);
    setPhaseCloseIncrement(0.5);
    setPhaseOutcomeIncrement(0.5);
    setManualPhasePoints(createManualPhasePoints());
    setFirstScorerEnabled(preset.firstScorerEnabled);
    setFirstScorerPlayerPoints(3);
    setFirstScorerTeamPoints(1);
    setFirstScorerProgressionEnabled(preset.firstScorerEnabled);
    setFirstScorerPlayerIncrement(1);
    setFirstScorerTeamIncrement(0.5);
    setAppliedScoringPreset(preset.key);
  };

  const markScoringCustom = () => {
    setAppliedScoringPreset("");
  };

  const openLeagueDetails = async () => {
    if (!hasActiveLeague || !room?.code) return;
    navigate(`/fifa/${encodeURIComponent(room.code)}`);
  };

  const copyLeagueCode = async () => {
    const value = leagueDetails?.room?.code || data?.room?.code || String(leagueCode || "").toUpperCase();
    if (!value || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(value);
      setNotice({ type: "success", text: `Skopiowano kod ligi: ${value}.` });
    } catch {
      setError("Nie udało się skopiować kodu ligi.");
    }
  };

  const copyInviteLink = async (leagueCodeValue) => {
    const value = String(
      leagueCodeValue ||
        leagueDetails?.room?.code ||
        data?.room?.code ||
        leagueCode ||
        ""
    )
      .trim()
      .toUpperCase();
    if (!value || !navigator?.clipboard?.writeText) return;
    const inviteUrl = `${window.location.origin}/fifa-zaproszenie/${encodeURIComponent(value)}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setNotice({ type: "success", text: "Skopiowano link zaproszenia do ligi." });
    } catch {
      setError("Nie udało się skopiować linku zaproszenia.");
    }
  };

  const copyCreatedCode = async () => {
    if (!createdCode || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(createdCode);
      setNotice({ type: "success", text: `Skopiowano kod ligi: ${createdCode}.` });
    } catch {
      setError("Nie udało się skopiować kodu ligi.");
    }
  };

  const saveLeagueTitle = async () => {
    const nextTitle = String(detailsTitle || "").trim();
    const codeToUpdate = leagueDetails?.room?.code || data?.room?.code || String(leagueCode || "").toUpperCase();
    if (!nextTitle || !codeToUpdate) return;
    setDetailsTitleSaving(true);
    setError("");
    setNotice(null);
    try {
      const payload = await updateFifaRoom(codeToUpdate, { title: nextTitle });
      setLeagueDetails(payload);
      setDetailsTitle(payload.room?.title || nextTitle);
      setDetailsTitleEditing(false);
      setData((prev) => ({
        ...(prev || {}),
        room: {
          ...(prev?.room || {}),
          ...(payload.room || {}),
        },
      }));
      setNotice({ type: "success", text: "Nazwa ligi została zmieniona." });
    } catch (err) {
      setError(fifaErrorMessage(err, "Nie udało się zmienić nazwy ligi."));
    } finally {
      setDetailsTitleSaving(false);
    }
  };

  const decideHistoricalImport = async (participantUserId, approved) => {
    const leagueCodeValue = leagueDetails?.room?.code || data?.room?.code;
    if (!leagueCodeValue) return;
    setError("");
    try {
      const payload = await decideFifaHistoricalImport(
        leagueCodeValue,
        participantUserId,
        approved
      );
      setLeagueDetails(payload);
      setNotice({
        type: "success",
        text: approved
          ? "Wcześniejsze typy zostały dopuszczone do rankingu."
          : "Wcześniejsze typy nie będą liczone w tej lidze.",
      });
    } catch (err) {
      setError(fifaErrorMessage(err, "Nie udało się zapisać decyzji."));
    }
  };

  if (loading) {
    return <FifaSkeleton />;
  }

  if (detailsMode) {
    const detailsRoom = leagueDetails?.room || data?.room || {};
    const detailsCode = detailsRoom.code || String(leagueCode || "").toUpperCase();
    const detailsPlayersCount = detailsRoom.participantsCount ?? leagueDetails?.participants?.length ?? 0;
    const canManageLeague = !!detailsRoom.canManage;
    const scoringRules = detailsRoom.scoringRules || {};

    return (
      <div className="fifa-shell">
        <div className="glass-container fifa-page">
          <div className="fifa-topbar fifa-topbar--details">
            <div>
              {canManageLeague && detailsTitleEditing ? (
                <div className="fifa-title-edit">
                  <input
                    className="input-secondary"
                    value={detailsTitle}
                    autoFocus
                    onChange={(e) => setDetailsTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveLeagueTitle();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setDetailsTitle(detailsRoom.title || "");
                        setDetailsTitleEditing(false);
                      }
                    }}
                    aria-label="Nazwa ligi"
                  />
                  <button className="btn btn-primary" type="button" disabled={detailsTitleSaving || !detailsTitle.trim()} onClick={saveLeagueTitle}>
                    {detailsTitleSaving ? "Zapisuję..." : "Zapisz"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={detailsTitleSaving}
                    onClick={() => {
                      setDetailsTitle(detailsRoom.title || "");
                      setDetailsTitleEditing(false);
                    }}
                  >
                    Anuluj
                  </button>
                </div>
              ) : (
                <h1 className="fifa-title">{detailsRoom.title || "Liga"}</h1>
              )}
              <p className="fifa-muted">
                {detailsCode} · {detailsPlayersCount} graczy
                {detailsRoom.isOwner ? " · Administrator ligi" : ""}
              </p>
              <p className="fifa-muted">
                Punktacja: {scoringRulesSummary(scoringRules)}.
              </p>
              {scoringRules.phaseProgression?.mode === "manual" ? (
                <p className="fifa-muted">
                  Punktacja faz została ustawiona ręcznie przez administratora
                  ligi.
                </p>
              ) : scoringRules.phaseProgression?.enabled ? (
                <p className="fifa-muted">
                  Rosnąca punktacja: każda kolejna faza +
                  {formatPointValue(scoringRules.phaseProgression.exactIncrement ?? 1)} pkt za dokładny wynik
                  {scoringRules.closeScoreEnabled === true
                    ? `, +${formatPointValue(scoringRules.phaseProgression.closeIncrement ?? 0.5)} pkt za bliski wynik`
                    : ""}
                  {" "}i +{formatPointValue(scoringRules.phaseProgression.outcomeIncrement ?? 0.5)} pkt za rezultat.
                </p>
              ) : null}
              {scoringRules.firstScorer?.enabled ? (
                <p className="fifa-muted">
                  Pierwszy strzelec: {scoringRules.firstScorer.playerPoints ?? 3} pkt za dokładnego zawodnika,
                  brak gola lub poprawny samobój; {scoringRules.firstScorer.teamPoints ?? 1} pkt za właściwą drużynę.
                </p>
              ) : null}
              {scoringRules.firstScorer?.enabled &&
              scoringRules.firstScorer.phaseProgression?.enabled ? (
                <p className="fifa-muted">
                  Rosnąca punktacja strzelca: każda kolejna faza +
                  {formatPointValue(scoringRules.firstScorer.phaseProgression.playerIncrement ?? 1)} pkt za zawodnika
                  i +{formatPointValue(scoringRules.firstScorer.phaseProgression.teamIncrement ?? 0.5)} pkt za drużynę.
                </p>
              ) : null}
              {scoringRules.tournamentSpecials?.enabled ? (
                <p className="fifa-muted">
                  Zakłady turniejowe:{" "}
                  {scoringRules.tournamentSpecials.championPoints ?? 10} pkt za
                  mistrza świata i{" "}
                  {scoringRules.tournamentSpecials.goldenBootPoints ?? 12} pkt
                  za króla strzelców.
                </p>
              ) : null}
            </div>
            <div className="fifa-details-top-actions" role="toolbar" aria-label="Narzędzia ligi">
              {canManageLeague && !detailsTitleEditing ? (
                <QuickIconButton
                  label={<PenIcon />}
                  title="Zmień nazwę ligi"
                  onClick={() => setDetailsTitleEditing(true)}
                />
              ) : null}
              <QuickIconButton
                label={<CopyCodeIcon />}
                title={`Kopiuj kod ligi (${detailsCode})`}
                onClick={copyLeagueCode}
              />
              <QuickIconButton
                label={<LinkChainIcon />}
                title="Kopiuj link zaproszenia"
                onClick={() => copyInviteLink(detailsCode)}
              />
              <QuickIconButton
                label={<LeaveDoorIcon />}
                title="Wróć do typera"
                onClick={() => navigate("/fifa")}
              />
            </div>
          </div>

          {notice ? <div className={`panel-card fifa-panel fifa-notice fifa-notice--${notice.type}`}>{notice.text}</div> : null}
          {error ? <div className="panel-card fifa-panel fifa-error">{error}</div> : null}
          {leagueDetails ? (
            <LeagueDetailsPanel
              details={leagueDetails}
              standalone
              onClose={() => navigate("/fifa")}
              onHistoricalDecision={decideHistoricalImport}
            />
          ) : (
            <div className="panel-card fifa-panel">
              <strong>Nie udalo sie otworzyc szczegolow tej ligi.</strong>
              <p className="fifa-muted">Sprawdz, czy jestes w tej lidze i czy backend jest uruchomiony z aktualnym kodem.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fifa-shell">
      <div className="glass-container fifa-page">
        <div className="fifa-topbar">
          <div>
            <h1 className="fifa-title">{room?.title || "Typer FIFA"}</h1>
            <p className="fifa-muted">
              {hasActiveLeague
                ? `${scoringRulesSummary(room?.scoringRules)}. `
                : "Dołącz do ligi albo utwórz własną, żeby typować i zobaczyć ranking. "}
              Terminarz:{" "}
              {data?.scheduleMeta?.officialMatchesCount ??
                data?.scheduleMeta?.matchesCount ??
                matches.length}
              /
              {data?.scheduleMeta?.expectedMatchesCount || 104} meczów turnieju.
              {data?.scheduleMeta?.testMatchesCount
                ? ` Dodatkowo: ${data.scheduleMeta.testMatchesCount} mecze testowe.`
                : ""}
            </p>
          </div>
          {!room?.isPredictionWorkspace ? <div className="fifa-code">{room?.code}</div> : null}
        </div>

        {guestMode ? (
          <div className="panel-card fifa-panel fifa-notice fifa-notice--info">
            <strong>Oglądasz pełny typer w trybie bez konta.</strong>
            <p className="fifa-muted">
              Możesz testować ustawienia punktacji, filtry i wpisywać próbne typy. Zapis,
              tworzenie ligi oraz dołączanie kodem wymagają rejestracji.
            </p>
            <Link
              className="btn btn-primary"
              to="/rejestracja"
              style={{ display: "inline-flex", marginTop: 10, textDecoration: "none" }}
            >
              Załóż konto
            </Link>
          </div>
        ) : null}

        {notice ? <div className={`panel-card fifa-panel fifa-notice fifa-notice--${notice.type}`}>{notice.text}</div> : null}
        {error ? <div className="panel-card fifa-panel fifa-error">{error}</div> : null}

        <div className="panel-card fifa-panel fifa-roombar">
          <div>
            <strong>Dołącz do ligi kodem.</strong>
            <p className="fifa-muted">
              Wpisz kod od organizatora. Po dołączeniu liga pojawi się w sekcji Twoje ligi.
            </p>
          </div>
          <div className="fifa-join-box">
            <div className="fifa-inline-form">
              <input
                className="input-secondary"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Kod"
                disabled={guestMode}
                style={{ width: 150, textTransform: "uppercase" }}
              />
              <button className="btn btn-secondary" type="button" disabled={guestMode} onClick={joinRoom}>
                Dołącz
              </button>
            </div>
            {joinNotice ? (
              <div className={`fifa-inline-message fifa-inline-message--${joinNotice.type}`}>
                {joinNotice.text}
              </div>
            ) : null}
          </div>
        </div>

        <div className="fifa-summary-grid fifa-summary-grid--hidden">
          {hasActiveLeague ? (
            <div className="panel-card">
              <strong>Twój wynik w tej lidze</strong>
              <div className="fifa-big-stat">{myPoints} pkt</div>
              <p className="fifa-muted">{myRankLabel}</p>
            </div>
          ) : (
            <div className="panel-card">
              <strong>Brak wybranej ligi</strong>
              <p className="fifa-muted">Wpisz kod ligi albo utwórz nową ligę. Po dołączeniu pojawi się tutaj Twój wynik.</p>
            </div>
          )}
          <div className="panel-card">
            <strong>Twoje ligi</strong>
            <div className="fifa-league-list">
                {myRooms.length ? (
                  myRooms.map((league) => (
                    <div className="fifa-league-item" key={league.id}>
                      <button
                        className={league.code === room?.code ? "fifa-league-row fifa-league-row--active" : "fifa-league-row"}
                        type="button"
                        onClick={async () => {
                          setError("");
                          setCode(league.code);
                          try {
                            setData(await joinFifaRoom(league.code));
                          } catch (err) {
                            setError(err.message || "Nie udało się otworzyć ligi.");
                          }
                        }}
                      >
                        <span>
                          <b>{league.title}</b>
                          <small>
                            {league.code} · {league.participantsCount} graczy
                            {league.isOwner ? " · Administrator ligi" : ""}
                          </small>
                        </span>
                        <span>{league.rank ? `${league.rank}.` : "-"} · {league.points} pkt</span>
                      </button>
                      {league.code === room?.code ? (
                        <div className="fifa-league-actions">
                          <p className="fifa-muted">
                            Punktacja: {scoringRulesSummary(room?.scoringRules)}.
                          </p>
                          <div className="fifa-league-preview">
                            {leaguePreviewRows.length ? (
                              leaguePreviewRows.map((row, index) => (
                                <div className="fifa-row" key={row.userId || row.displayName}>
                                  <span className="fifa-rank">{row.rank || index + 1}</span>
                                  <span className="fifa-preview-name" title={row.displayName}>
                                    {row.displayName}
                                  </span>
                                  <span className="fifa-points">{row.points || 0} pkt</span>
                                </div>
                              ))
                            ) : (
                              <p className="fifa-muted">Tabela pojawi się po dołączeniu graczy.</p>
                            )}
                          </div>
                          <button className="btn btn-secondary fifa-details-btn" type="button" onClick={openLeagueDetails}>
                            Pokaż więcej szczegółów
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="fifa-muted">Dołącz albo utwórz pierwszą ligę.</p>
              )}
            </div>
          </div>
        </div>

        <div className="panel-card fifa-panel">
          <div className="fifa-collapsible-head">
            <strong>Nowa liga</strong>
            <button
              className="btn btn-secondary fifa-icon-btn"
              type="button"
              onClick={() => setCreateLeagueOpen((open) => !open)}
              aria-expanded={createLeagueOpen}
              aria-label={createLeagueOpen ? "Zwiń tworzenie ligi" : "Rozwiń tworzenie ligi"}
              title={createLeagueOpen ? "Zwiń" : "Rozwiń"}
            >
              {createLeagueOpen ? "-" : "+"}
            </button>
          </div>
          {createLeagueOpen ? (
            <>
              <div className="fifa-create-grid">
                <div className="fifa-scoring-presets">
                  <strong>Gotowe warianty</strong>
                  <div className="fifa-scoring-preset-grid">
                    {SCORING_PRESETS.map((preset) => (
                      <button
                        className={
                          appliedScoringPreset === preset.key
                            ? "btn btn-primary fifa-scoring-preset"
                            : "btn btn-secondary fifa-scoring-preset"
                        }
                        type="button"
                        key={preset.key}
                        onClick={() => applyScoringPreset(preset)}
                      >
                        <span>{preset.label}</span>
                        <small>{preset.description}</small>
                      </button>
                    ))}
                  </div>
                  <p className="fifa-muted">
                    Szablony włączają rosnącą punktację. Po wyborze możesz zmienić każde ustawienie ręcznie.
                  </p>
                  <label className="fifa-progression-toggle fifa-specials-master-toggle">
                    <input
                      type="checkbox"
                      checked={tournamentSpecialsEnabled}
                      onChange={(event) =>
                        setTournamentSpecialsEnabled(event.target.checked)
                      }
                    />
                    <span>Zakłady turniejowe: mistrz świata i król strzelców</span>
                  </label>
                  {tournamentSpecialsEnabled ? (
                    <div className="fifa-specials-config">
                      <label className="fifa-field">
                        <span className="fifa-label">Mistrz świata</span>
                        <input
                          className="input-secondary"
                          type="number"
                          min="0"
                          max="30"
                          step="0.5"
                          value={championPoints}
                          onChange={(event) =>
                            setChampionPoints(event.target.value)
                          }
                        />
                      </label>
                      <label className="fifa-field">
                        <span className="fifa-label">Król strzelców</span>
                        <input
                          className="input-secondary"
                          type="number"
                          min="0"
                          max="30"
                          step="0.5"
                          value={goldenBootPoints}
                          onChange={(event) =>
                            setGoldenBootPoints(event.target.value)
                          }
                        />
                      </label>
                      <p className="fifa-muted fifa-create-note">
                        Domyślnie 10 pkt za mistrza i 12 pkt za oficjalnego
                        zdobywcę Złotego Buta FIFA. Typowanie zamyka się 5 minut
                        przed meczem otwarcia.
                      </p>
                    </div>
                  ) : null}
                </div>
                <label className="fifa-field fifa-field--title">
                  <span className="fifa-label">Nazwa</span>
                  <input className="input-secondary" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
                </label>
                <label className="fifa-field">
                  <span className="fifa-label">Dokładny</span>
                  <input
                    className="input-secondary"
                    type="number"
                    min="0"
                    max="30"
                    step="0.5"
                    value={exactScore}
                    onChange={(e) => {
                      markScoringCustom();
                      setExactScore(e.target.value);
                    }}
                  />
                </label>
                <label className="fifa-field">
                  <span className="fifa-label">Rezultat</span>
                  <input
                    className="input-secondary"
                    type="number"
                    min="0"
                    max="30"
                    step="0.5"
                    value={correctOutcome}
                    onChange={(e) => {
                      markScoringCustom();
                      setCorrectOutcome(e.target.value);
                    }}
                  />
                </label>
                <label className="fifa-progression-toggle">
                  <input
                    type="checkbox"
                    checked={closeScoreEnabled}
                    onChange={(e) => {
                      markScoringCustom();
                      setCloseScoreEnabled(e.target.checked);
                    }}
                  />
                  <span>Punktacja za bliski wynik</span>
                </label>
                {closeScoreEnabled ? (
                  <>
                    <label className="fifa-field">
                      <span className="fifa-label">Bliski wynik</span>
                      <input
                        className="input-secondary"
                        type="number"
                        min="0"
                        max="30"
                        step="0.5"
                        value={closeScore}
                        onChange={(e) => {
                          markScoringCustom();
                          setCloseScore(e.target.value);
                        }}
                      />
                    </label>
                    <p className="fifa-muted fifa-create-note">
                      Opcjonalna kategoria między dokładnym wynikiem a rezultatem. Typ musi
                      wskazywać właściwego zwycięzcę albo remis i być pomylony najwyżej o jednego
                      gola jednej lub obu drużyn.
                    </p>
                    <details className="fifa-close-examples">
                      <summary>Zobacz przykłady</summary>
                      <div className="fifa-close-examples-grid">
                        <div className="fifa-close-example">
                          <strong>Wynik meczu: 2:1</strong>
                          <span>Bliskie: 1:0, 2:0, 3:1, 3:2</span>
                          <span>Tylko rezultat: 4:1</span>
                          <span>Nietrafione: 1:1</span>
                        </div>
                        <div className="fifa-close-example">
                          <strong>Wynik meczu: 1:1</strong>
                          <span>Bliskie: 0:0, 2:2</span>
                          <span>Tylko rezultat: 3:3</span>
                          <span>Nietrafione: 2:1</span>
                        </div>
                        <div className="fifa-close-example">
                          <strong>Wynik meczu: 0:2</strong>
                          <span>Bliskie: 0:1, 0:3, 1:2, 1:3</span>
                          <span>Tylko rezultat: 0:4</span>
                          <span>Nietrafione: 2:1</span>
                        </div>
                      </div>
                    </details>
                  </>
                ) : null}
                <label className="fifa-progression-toggle">
                  <input
                    type="checkbox"
                    checked={phaseScoringMode !== "flat"}
                    onChange={(e) => {
                      markScoringCustom();
                      setPhaseScoringMode(
                        e.target.checked ? "progressive" : "flat"
                      );
                    }}
                  />
                  <span>Rosnąca punktacja w kolejnych fazach</span>
                </label>
                {phaseScoringMode !== "flat" ? (
                  <>
                    {phaseScoringMode === "progressive" ? (
                      <>
                        <label className="fifa-field">
                          <span className="fifa-label">Przyrost dokładnego</span>
                          <input
                            className="input-secondary"
                            type="number"
                            min="0"
                            max="30"
                            step="0.5"
                            value={phaseExactIncrement}
                            onChange={(e) => {
                              markScoringCustom();
                              setPhaseExactIncrement(e.target.value);
                            }}
                          />
                        </label>
                        {closeScoreEnabled ? (
                          <label className="fifa-field">
                            <span className="fifa-label">Przyrost bliskiego</span>
                            <input
                              className="input-secondary"
                              type="number"
                              min="0"
                              max="30"
                              step="0.5"
                              value={phaseCloseIncrement}
                              onChange={(e) => {
                                markScoringCustom();
                                setPhaseCloseIncrement(e.target.value);
                              }}
                            />
                          </label>
                        ) : null}
                        <label className="fifa-field">
                          <span className="fifa-label">Przyrost rezultatu</span>
                          <input
                            className="input-secondary"
                            type="number"
                            min="0"
                            max="30"
                            step="0.5"
                            value={phaseOutcomeIncrement}
                            onChange={(e) => {
                              markScoringCustom();
                              setPhaseOutcomeIncrement(e.target.value);
                            }}
                          />
                        </label>
                      </>
                    ) : null}
                    <div className="fifa-scoring-preview">
                      <div className="fifa-scoring-preview-title">
                        <strong>Podgląd punktacji</strong>
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => {
                            markScoringCustom();
                            if (phaseScoringMode === "manual") {
                              setPhaseScoringMode("progressive");
                              return;
                            }
                            setManualPhasePoints(
                              Object.fromEntries(
                                SCORING_PREVIEW_PHASES.map((phase) => [
                                  phase.key,
                                  {
                                    exactScore:
                                      scoringPreviewValue(exactScore) +
                                      phase.level *
                                        scoringPreviewValue(phaseExactIncrement),
                                    closeScore:
                                      scoringPreviewValue(closeScore) +
                                      phase.level *
                                        scoringPreviewValue(phaseCloseIncrement),
                                    correctOutcome:
                                      scoringPreviewValue(correctOutcome) +
                                      phase.level *
                                        scoringPreviewValue(phaseOutcomeIncrement),
                                  },
                                ])
                              )
                            );
                            setPhaseScoringMode("manual");
                          }}
                        >
                          {phaseScoringMode === "manual"
                            ? "Wróć do przyrostu"
                            : "Ustaw ręcznie"}
                        </button>
                      </div>
                      <div
                        className={`fifa-scoring-preview-table${
                          closeScoreEnabled ? " fifa-scoring-preview-table--result" : ""
                        }${
                          phaseScoringMode === "manual"
                            ? " fifa-scoring-preview-table--editable"
                            : ""
                        }`}
                      >
                        <div className="fifa-scoring-preview-head">Faza</div>
                        <div className="fifa-scoring-preview-head">Dokładny</div>
                        {closeScoreEnabled ? (
                          <div className="fifa-scoring-preview-head">Bliski</div>
                        ) : null}
                        <div className="fifa-scoring-preview-head">Rezultat</div>
                        {SCORING_PREVIEW_PHASES.map((phase) => {
                          const automatic = {
                            exactScore:
                              scoringPreviewValue(exactScore) +
                              phase.level *
                                scoringPreviewValue(phaseExactIncrement),
                            closeScore:
                              scoringPreviewValue(closeScore) +
                              phase.level *
                                scoringPreviewValue(phaseCloseIncrement),
                            correctOutcome:
                              scoringPreviewValue(correctOutcome) +
                              phase.level *
                                scoringPreviewValue(phaseOutcomeIncrement),
                          };
                          const values =
                            phaseScoringMode === "manual"
                              ? manualPhasePoints[phase.key] || automatic
                              : automatic;
                          const renderValue = (field) =>
                            phaseScoringMode === "manual" ? (
                              <input
                                className="input-secondary fifa-manual-score-input"
                                type="number"
                                min="0"
                                max="30"
                                step="0.5"
                                value={values[field]}
                                aria-label={`${phase.label} - ${field}`}
                                onChange={(event) => {
                                  markScoringCustom();
                                  setManualPhasePoints((current) => ({
                                    ...current,
                                    [phase.key]: {
                                      ...(current[phase.key] || automatic),
                                      [field]: event.target.value,
                                    },
                                  }));
                                }}
                              />
                            ) : (
                              `${formatPointValue(values[field])} pkt`
                            );
                          return (
                            <React.Fragment key={phase.key}>
                              <div className="fifa-scoring-preview-phase">
                                {phase.label}
                              </div>
                              <div>{renderValue("exactScore")}</div>
                              {closeScoreEnabled ? (
                                <div>{renderValue("closeScore")}</div>
                              ) : null}
                              <div>{renderValue("correctOutcome")}</div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : null}
                <label className="fifa-progression-toggle">
                  <input
                    type="checkbox"
                    checked={firstScorerEnabled}
                    onChange={(e) => {
                      markScoringCustom();
                      setFirstScorerEnabled(e.target.checked);
                    }}
                  />
                  <span>Dodatkowa punktacja za pierwszego strzelca</span>
                </label>
                {firstScorerEnabled ? (
                  <>
                    <label className="fifa-field">
                      <span className="fifa-label">Dokładny zawodnik</span>
                      <input
                        className="input-secondary"
                        type="number"
                        min="0"
                        max="30"
                        step="0.5"
                        value={firstScorerPlayerPoints}
                        onChange={(e) => {
                          markScoringCustom();
                          setFirstScorerPlayerPoints(e.target.value);
                        }}
                      />
                    </label>
                    <label className="fifa-field">
                      <span className="fifa-label">Pierwsza drużyna</span>
                      <input
                        className="input-secondary"
                        type="number"
                        min="0"
                        max="30"
                        step="0.5"
                        value={firstScorerTeamPoints}
                        onChange={(e) => {
                          markScoringCustom();
                          setFirstScorerTeamPoints(e.target.value);
                        }}
                      />
                    </label>
                    <p className="fifa-muted fifa-create-note">
                      Dokładny zawodnik, brak gola albo poprawny samobój daje pełną pulę.
                      Trafienie tylko drużyny, która strzeli pierwsza, daje mniejszą pulę.
                    </p>
                    <label className="fifa-progression-toggle">
                      <input
                        type="checkbox"
                        checked={firstScorerProgressionEnabled}
                        onChange={(e) => {
                          markScoringCustom();
                          setFirstScorerProgressionEnabled(e.target.checked);
                        }}
                      />
                      <span>Rosnąca punktacja strzelca w kolejnych fazach</span>
                    </label>
                    {firstScorerProgressionEnabled ? (
                      <>
                        <label className="fifa-field">
                          <span className="fifa-label">Przyrost zawodnika</span>
                          <input
                            className="input-secondary"
                            type="number"
                            min="0"
                            max="30"
                            step="0.5"
                            value={firstScorerPlayerIncrement}
                            onChange={(e) => {
                              markScoringCustom();
                              setFirstScorerPlayerIncrement(e.target.value);
                            }}
                          />
                        </label>
                        <label className="fifa-field">
                          <span className="fifa-label">Przyrost drużyny</span>
                          <input
                            className="input-secondary"
                            type="number"
                            min="0"
                            max="30"
                            step="0.5"
                            value={firstScorerTeamIncrement}
                            onChange={(e) => {
                              markScoringCustom();
                              setFirstScorerTeamIncrement(e.target.value);
                            }}
                          />
                        </label>
                        <div className="fifa-scoring-preview">
                          <strong>Podgląd punktacji strzelca</strong>
                          <div className="fifa-scoring-preview-table">
                            <div className="fifa-scoring-preview-head">Faza</div>
                            <div className="fifa-scoring-preview-head">Zawodnik</div>
                            <div className="fifa-scoring-preview-head">Drużyna</div>
                            {SCORING_PREVIEW_PHASES.map((phase) => (
                              <React.Fragment key={`scorer-${phase.label}`}>
                                <div className="fifa-scoring-preview-phase">{phase.label}</div>
                                <div>
                                  {formatPointValue(
                                    scoringPreviewValue(firstScorerPlayerPoints) +
                                      phase.level *
                                        scoringPreviewValue(firstScorerPlayerIncrement)
                                  )}{" "}
                                  pkt
                                </div>
                                <div>
                                  {formatPointValue(
                                    scoringPreviewValue(firstScorerTeamPoints) +
                                      phase.level *
                                        scoringPreviewValue(firstScorerTeamIncrement)
                                  )}{" "}
                                  pkt
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : null}
                <button className="btn btn-primary fifa-create-btn" type="button" disabled={guestMode || creatingRoom} onClick={createRoom}>
                  {creatingRoom ? "Tworzę..." : "Utwórz ligę"}
                </button>
              </div>
              {createdCode ? (
                <div className="fifa-created-league">
                  <div>
                    <strong>Utworzono ligę. Zaproś uczestników</strong>
                    <p className="fifa-muted">
                      Kod ligi: <b>{createdCode}</b>. Kod i link znajdziesz również później w szczegółach ligi.
                    </p>
                  </div>
                  <div className="fifa-created-league-actions">
                    <button className="btn btn-secondary" type="button" onClick={copyCreatedCode}>
                      Kopiuj kod
                    </button>
                    <button className="btn btn-primary" type="button" onClick={() => copyInviteLink(createdCode)}>
                      Kopiuj link zaproszenia
                    </button>
                  </div>
                </div>
              ) : null}
              <p className="fifa-muted">
                Typowanie blokuje się globalnie 5 minut przed pierwszym gwizdkiem. Kod ligi generuje się automatycznie po utworzeniu
                .
              </p>
            </>
          ) : null}
        </div>

        {room?.isPredictionWorkspace &&
        room?.scoringRules?.tournamentSpecials?.enabled ? (
          <TournamentSpecialBetsPanel
            room={room}
            playersByTeam={data?.playersByTeam || {}}
            value={specialDraft}
            savedValue={data?.specialPrediction}
            saving={savingSpecial}
            onChange={setSpecialDraft}
            onSave={saveSpecialPrediction}
            adminResult={specialAdminResult}
            onAdminResultChange={setSpecialAdminResult}
            onSaveAdminResult={saveSpecialResult}
          />
        ) : hasActiveLeague ? (
          room?.scoringRules?.tournamentSpecials?.enabled ? (
            <TournamentSpecialsLeaguePreview
              room={room}
              prediction={data?.specialPrediction}
              onOpenMain={openPredictionWorkspace}
            />
          ) : (
            <TournamentSpecialsUnavailablePanel
              room={room}
              saving={enablingSpecials}
              onEnable={enableTournamentSpecials}
            />
          )
        ) : null}

        <div className="panel-card fifa-panel fifa-view-panel">
          <div className="fifa-actions-row">
            <div className="fifa-segmented" role="group" aria-label="Sortowanie meczów">
              <button
                className={sortMode === "groups" ? "btn btn-primary" : "btn btn-secondary"}
                type="button"
                onClick={() => setSortMode("groups")}
              >
                Grupy
              </button>
              <button
                className={sortMode === "time" ? "btn btn-primary" : "btn btn-secondary"}
                type="button"
                onClick={() => setSortMode("time")}
              >
                Chronologicznie
              </button>
            </div>
            <label className="fifa-multi-toggle">
              <input
                type="checkbox"
                checked={mainMultiSelect}
                onChange={toggleMainMultiSelect}
              />
              <span>Zaznaczaj wiele</span>
            </label>
            <button
              className="btn btn-primary"
              type="button"
              disabled={!canPredict || savingAll || !hasSavableDrafts}
              onClick={saveAllPredictions}
            >
              {savingAll ? "Zapisuję..." : "Zapisz wszystkie typy"}
            </button>
          </div>
          <div className="fifa-filterbar">
            {sortMode === "groups" ? (
              <>
                <strong>Grupa</strong>
                <div className="fifa-chip-row" role="group" aria-label="Wybór grupy">
                  {groupOptions.map((group) => (
                    <button
                      key={group}
                      className={
                        mainMultiSelect
                          ? selectedGroups.includes(group)
                            ? "btn btn-primary"
                            : "btn btn-secondary"
                          : selectedGroup === group
                            ? "btn btn-primary"
                            : "btn btn-secondary"
                      }
                      type="button"
                      onClick={() =>
                        mainMultiSelect
                          ? toggleSelectedValue(setSelectedGroups, group)
                          : setSelectedGroup(group)
                      }
                    >
                      {group === "TEST" ? "Test" : group}
                    </button>
                  ))}
                  <button
                    className={
                      mainMultiSelect
                        ? selectedGroups.includes("knockout")
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                        : selectedGroup === "knockout"
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                    }
                    type="button"
                    onClick={() =>
                      mainMultiSelect
                        ? toggleSelectedValue(setSelectedGroups, "knockout")
                        : setSelectedGroup("knockout")
                    }
                  >
                    Faza pucharowa
                  </button>
                  <button
                    className={
                      mainMultiSelect
                        ? !selectedGroups.length
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                        : selectedGroup === "all"
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                    }
                    type="button"
                    onClick={() =>
                      mainMultiSelect ? setSelectedGroups([]) : setSelectedGroup("all")
                    }
                  >
                    Wszystkie
                  </button>
                </div>
                {(mainMultiSelect
                  ? selectedGroups.includes("knockout")
                  : selectedGroup === "knockout") ? (
                  <div className="fifa-subfilter">
                    <strong>Etap</strong>
                    <div className="fifa-chip-row" role="group" aria-label="Wybór etapu fazy pucharowej">
                      {KNOCKOUT_STAGE_ORDER.map((stage) => (
                        <button
                          key={stage}
                          className={
                            mainMultiSelect
                              ? selectedKnockoutStages.includes(stage)
                                ? "btn btn-primary"
                                : "btn btn-secondary"
                              : selectedKnockoutStage === stage
                                ? "btn btn-primary"
                                : "btn btn-secondary"
                          }
                          type="button"
                          onClick={() =>
                            mainMultiSelect
                              ? toggleSelectedValue(setSelectedKnockoutStages, stage)
                              : setSelectedKnockoutStage(stage)
                          }
                        >
                          {stageLabel(stage)}
                        </button>
                      ))}
                      <button
                        className={
                          mainMultiSelect
                            ? !selectedKnockoutStages.length
                              ? "btn btn-primary"
                              : "btn btn-secondary"
                            : selectedKnockoutStage === "all"
                              ? "btn btn-primary"
                              : "btn btn-secondary"
                        }
                        type="button"
                        onClick={() =>
                          mainMultiSelect
                            ? setSelectedKnockoutStages([])
                            : setSelectedKnockoutStage("all")
                        }
                      >
                        Wszystkie etapy
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="fifa-day-filter-heading">
                  <strong>Dzień</strong>
                  {!dayListExpanded && compactDayOptions[0] ? (
                    <span className="fifa-muted">
                      Terminy od {compactDayOptions[0].label}
                    </span>
                  ) : null}
                </div>
                <div
                  className={`fifa-day-grid${dayListExpanded ? " fifa-day-grid--expanded" : ""}`}
                  role="group"
                  aria-label="Wybór dnia"
                >
                  {compactDayOptions.map((day) => (
                    <button
                      key={day.dateKey}
                      className={
                        mainMultiSelect
                          ? selectedDays.includes(day.dateKey)
                            ? "btn btn-primary fifa-day-chip"
                            : "btn btn-secondary fifa-day-chip"
                          : selectedDay === day.dateKey
                            ? "btn btn-primary fifa-day-chip"
                            : "btn btn-secondary fifa-day-chip"
                      }
                      type="button"
                      onClick={() =>
                        mainMultiSelect
                          ? toggleSelectedValue(setSelectedDays, day.dateKey)
                          : setSelectedDay(day.dateKey)
                      }
                    >
                      <span className="fifa-day-chip-label">{day.label}</span>
                      <span className="fifa-day-chip-count">{day.count}</span>
                    </button>
                  ))}
                </div>
                <div className="fifa-day-actions">
                  <button
                    className={
                      mainMultiSelect
                        ? !selectedDays.length
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                        : selectedDay === "all"
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                    }
                    type="button"
                    onClick={() =>
                      mainMultiSelect ? setSelectedDays([]) : setSelectedDay("all")
                    }
                  >
                    Wszystkie dni
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    aria-expanded={dayListExpanded}
                    onClick={() => setDayListExpanded((expanded) => !expanded)}
                  >
                    {dayListExpanded ? "Zwiń daty" : "Pokaż wszystkie daty"}
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="fifa-current-view-progress">
            <strong>Aktualny widok</strong>
            <PredictionProgress
              matches={currentVisibleMatches}
              predictions={data?.predictions}
            />
          </div>
        </div>

        <div className="fifa-grid">
          <div className="fifa-match-list">
            {sortMode === "groups" ? (
              Object.entries(visibleGroupedMatches).map(([groupName, groupMatches]) => (
                <section key={groupName}>
                  <div className="fifa-section-heading">
                    <h2 className="fifa-section-title">{groupName}</h2>
                    <PredictionProgress matches={groupMatches} predictions={data?.predictions} />
                  </div>
                  <div className="fifa-match-list">
                    {groupMatches.map((match) => (
                      <MatchPrediction
                        key={match.id}
                        room={room}
                        match={match}
                        prediction={data?.predictions?.[match.id]}
                        draft={drafts[match.id]}
                        playersByTeam={data?.playersByTeam}
                        isAdmin={me.isAdmin}
                        onDraftChange={updateDraft}
                        onSavePrediction={saveOnePrediction}
                        onSaved={setData}
                        onError={setError}
                        previewMode={guestMode}
                      />
                    ))}
                  </div>
                </section>
              ))
            ) : (
              <section>
                <div className="fifa-section-heading">
                  <h2 className="fifa-section-title">
                    {mainMultiSelect
                      ? selectedDays.length
                        ? `Wybrane dni (${selectedDays.length})`
                        : "Wszystkie mecze"
                      : selectedDay === "all"
                        ? "Wszystkie mecze"
                        : dayOptions.find((day) => day.dateKey === selectedDay)?.label || "Mecze"}
                  </h2>
                  <PredictionProgress
                    matches={visibleChronologicalMatches}
                    predictions={data?.predictions}
                  />
                </div>
                <div className="fifa-match-list">
                  {visibleChronologicalMatches.map((match) => (
                    <MatchPrediction
                      key={match.id}
                      room={room}
                      match={match}
                      prediction={data?.predictions?.[match.id]}
                      draft={drafts[match.id]}
                      playersByTeam={data?.playersByTeam}
                      isAdmin={me.isAdmin}
                      onDraftChange={updateDraft}
                      onSavePrediction={saveOnePrediction}
                      onSaved={setData}
                      onError={setError}
                      previewMode={guestMode}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
          <aside className="fifa-sidebar">
            <div className="panel-card">
              <strong>Twoje ligi</strong>
              <p className="fifa-muted">Kliknij ligę, żeby zobaczyć jej szybki podgląd.</p>
              <div className="fifa-league-list">
                {myRooms.length ? (
                  myRooms.map((league) => (
                    <div className="fifa-league-item" key={league.id}>
                      <button
                        className={league.code === room?.code ? "fifa-league-row fifa-league-row--active" : "fifa-league-row"}
                        type="button"
                        onClick={async () => {
                          setError("");
                          setCode(league.code);
                          try {
                            setData(await joinFifaRoom(league.code));
                          } catch (err) {
                            setError(err.message || "Nie udało się otworzyć ligi.");
                          }
                        }}
                      >
                        <span>
                          <b>{league.title}</b>
                          <small>
                            {league.code} · {league.participantsCount} graczy
                            {league.isOwner ? " · Administrator ligi" : ""}
                          </small>
                        </span>
                        <span>{league.rank ? `${league.rank}.` : "-"} · {league.points} pkt</span>
                      </button>
                      {league.code === room?.code ? (
                        <div className="fifa-league-actions">
                          <p className="fifa-muted">
                            Punktacja: {scoringRulesSummary(room?.scoringRules)}.
                          </p>
                          <div className="fifa-league-preview">
                            {leaguePreviewRows.length ? (
                              leaguePreviewRows.map((row, index) => (
                                <div className="fifa-row" key={row.userId || row.displayName}>
                                  <span className="fifa-rank">{row.rank || index + 1}</span>
                                  <span className="fifa-preview-name" title={row.displayName}>
                                    {row.displayName}
                                  </span>
                                  <span className="fifa-points">{row.points || 0} pkt</span>
                                </div>
                              ))
                            ) : (
                              <p className="fifa-muted">Tabela pojawi się po dołączeniu graczy.</p>
                            )}
                          </div>
                          <button className="btn btn-secondary fifa-details-btn" type="button" onClick={openLeagueDetails}>
                            Pokaż więcej szczegółów
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="fifa-muted">Dołącz albo utwórz pierwszą ligę.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
        {leagueDetailsOpen ? (
          <LeagueDetailsPanel details={leagueDetails} onClose={() => setLeagueDetailsOpen(false)} />
        ) : null}
      </div>
    </div>
  );
}
